import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTheme } from "../../shared/contexts/ThemeContext";
import { X, Loader2, Trash2, LayoutGrid, Rows, FileText, Sparkles, ArrowRight, Megaphone, Bot, Users, Shield, MessageSquare, Eye, Home, Image as ImageIcon, MapPin, Mail, Video, Menu } from "lucide-react";
import Logo from "./Logo";
import SampleSummaryModal from "./SampleSummaryModal";
import CaseDetailsModal from "./CaseDetailsModal";
import KanbanBoard from "./KanbanBoard";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import { useCelebration, CelebrationOverlay } from "./CelebrationEffects";
import { normalizeCaseData } from "../utils/caseHelpers";
import { resolveFileLinkHelper } from "../utils/fileHelpers";
import DashboardHeader from "./DashboardHeader";
import DashboardStats from "./DashboardStats";
import CasesTable from "./CasesTable";
import CollabCasesList from "./CollabCasesList";
import TeamsManager from "./TeamsManager";
import LenderPerformanceDashboard from "./lenderDashboard/LenderPerformanceDashboard";
import Sidebar from "./Sidebar";
import PreviewModal from "./PreviewModal";
import AccountSettingsModal from "./AccountSettingsModal";
import NewCaseModal from "./NewCaseModal";
import LenderChat from "./LenderChat";
import MeetingAssistant from "./MeetingAssistant";
import IntakeAssistant from "./IntakeAssistant";
import MarketingAgent from "./MarketingAgent";
import LeadsDashboard from "./LeadsDashboard";
import ListingsDashboard from "./ListingsDashboard";
import PartnersDashboard from "./PartnersDashboard";
import RealtorReferralDashboard from "./RealtorReferralDashboard";
import InsuranceReferralDashboard from "./InsuranceReferralDashboard";
import DesignAgent from "./DesignAgent";
import VideoAgent from "./VideoAgent";
import VirtualStaging from "./VirtualStaging";
import AvatarIntro from "./AvatarIntro";
import TasksDashboard from "./TasksDashboard";
import TrainingPortal from "./TrainingPortal";
import SupportPage from "./SupportPage";
import TeamChat from "./TeamChat";
import SMSInbox from "./SMSInbox";
import ReportGenerationModal from "./ReportGenerationModal";
import TaskCopilotPrototype from "./TaskCopilotPrototype";
import DeveloperPortal from "./DeveloperPortal";
import AffiliatePortal from "./AffiliatePortal";
import NotesPage from "./NotesPage";
import CalendarPage from "./CalendarPage";
import AMAChat from "./AMAChat";
import SocialMediaManager from "./marketing/SocialMediaManager";
import SocialImageGenerator from "./marketing/SocialImageGenerator";
import FBMonitorDashboard from "./fbMonitor/FBMonitorDashboard";
import { MotionProjectsManager } from "./marketing/reels";
import { PhotoProjectsManager } from "./marketing/photo";
import AgentModal from "./AgentModal";
import UsageDashboard from "./UsageDashboard";
import { OrganizationManager } from "./organizations";
import ReportsAnalytics from "./ReportsAnalytics";
import PhoneCallModal from "./PhoneCallModal";
import { usePhoneCall } from "../hooks/usePhoneCall";
import AccountProvisioning from "./AccountProvisioning";
import ReferAndEarn from "./ReferAndEarn";
import DirectMailDashboard from "./DirectMail/DirectMailDashboard";
import SocialScheduler from "./SocialScheduler";

import { transformCasesToDashboardData } from "../utils/dashboardDataTransformer";
import { getFeatureFlags, normalizeTier } from "../utils/subscriptionHelpers";
import { useSession } from "../../shared/hooks/useSession";
import {
  createCase,
  updateCaseStatus,
  uploadCaseDocuments,
  requestAiReport,
  fetchDashboard,
  createCheckoutSession,
  createPortalSession,
  fetchCaseById,
  fetchSignedUrl,
  getStoredTokens,
  fetchPortfolioPnlSummary,
  fetchCollabCases,
  fetchUserSubscription,
  fetchUserDetails,
  fetchCreditPacks,
  deductSubscriptionCredits,
  fetchTeamMembers,
  addCaseComment,
  fetchCaseRuns,
  fetchRunStatus,
  deleteDocument,
} from "../api";
import { validateFiles, sanitizeFile } from "../../shared/utils/fileValidation";
import { ChatService } from "../services/ChatService";
import { getAccessToken, isTokenExpired } from "../../shared/utils/secureTokenStorage";
import { refreshAccessToken } from "../api/client";

const STARTER_LOOKUP_KEY = process.env.REACT_APP_STRIPE_STARTER_LOOKUP_KEY || "starter_monthly_v2";
const PRO_LOOKUP_KEY = process.env.REACT_APP_STRIPE_PRO_LOOKUP_KEY || "pro_monthly";
const PRO_PRICE_ID = process.env.REACT_APP_STRIPE_PRO_PRICE_ID || "price_1Scm38E9SKY77VxoadWy7n6g";
const TOPUP_2000_LOOKUP_KEY = process.env.REACT_APP_STRIPE_TOPUP_2000_LOOKUP_KEY || "credits_2000";
const TOPUP_6000_LOOKUP_KEY = process.env.REACT_APP_STRIPE_TOPUP_6000_LOOKUP_KEY || "credits_6000";
const TOPUP_30000_LOOKUP_KEY = process.env.REACT_APP_STRIPE_TOPUP_30000_LOOKUP_KEY || "credits_30000";

// Tier display information for checkout modals
const TIER_DISPLAY_INFO = {
  starter: {
    name: "Starter Membership",
    description: "50 cases • Fast processing • Priority support"
  },
  pro: {
    name: "Pro Membership",
    description: "150 cases • Priority processing • Dedicated support"
  },
  free: {
    name: "Free",
    description: "10 cases • Basic processing"
  }
};

const canonicalStatusMap = {
  "processing": "Processing",
  "underwriting": "Underwriting",
  "action required": "Action Required",
  "approved": "Approved",
  "cleared to close": "Cleared to Close",
  "closing": "Closing",
  "funded": "Funded",
  "fall out": "Fall out",
  "fallout": "Fall out"
};

const getCanonicalStatus = (status) => {
  const trimmed = typeof status === "string" ? status.trim() : "";
  if (!trimmed) return "";
  return canonicalStatusMap[trimmed.toLowerCase()] || trimmed;
};

// Module-level flag to prevent duplicate chat preload attempts across StrictMode remounts
let chatPreloadAttempted = false;

const CasesSkeleton = () => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden animate-pulse">
    <div className="border-b border-slate-800 bg-slate-900/50 p-4 flex gap-4">
      {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-4 bg-slate-800 rounded flex-1"></div>)}
    </div>
    {[1, 2, 3, 4, 5].map(i => (
      <div key={i} className="p-4 border-b border-slate-800/50 flex gap-4 items-center">
        <div className="h-4 w-20 bg-slate-800 rounded"></div>
        <div className="h-4 w-32 bg-slate-800 rounded flex-1"></div>
        <div className="h-4 w-24 bg-slate-800 rounded"></div>
        <div className="h-4 w-24 bg-slate-800 rounded"></div>
        <div className="h-4 w-24 bg-slate-800 rounded"></div>
        <div className="h-6 w-20 bg-slate-800 rounded-full"></div>
      </div>
    ))}
  </div>
);

const Dashboard = ({ userEmail, onLogout }) => {
  const { theme } = useTheme();
  const { width, height } = useWindowSize();
  const { triggerCelebration, celebrationState } = useCelebration();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [celebrationStatus, setCelebrationStatus] = useState(null);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);

  // Phone call state and handlers
  const phoneCall = usePhoneCall();

  // Handler to start a call from any component
  const handleStartPhoneCall = useCallback(({ phoneNumber, contactName, leadId, caseId }) => {
    phoneCall.startCall({ phoneNumber, contactName, leadId, caseId });
  }, [phoneCall]);

  const handleSessionExpire = useCallback(() => {
    if (onLogout) {
      onLogout(true); // Pass true for a silent logout
    }
  }, [onLogout]);

  useSession(userEmail, handleSessionExpire);

  const DASHBOARD_TIMEOUT = 15000; // 15 seconds - fail fast
  const USER_TIMEOUT = 10000; // 10 seconds for subscription - fail fast
  const [searchTerm, setSearchTerm] = useState("");
  const [cases, setCases] = useState([]);
  const [isNewCaseOpen, setIsNewCaseOpen] = useState(false);
  const [caseForm, setCaseForm] = useState({
    name: "",
    email: "",
    phone: "",
    propertyAddress: "",
    loanType: "Conventional",
    loanAmount: "",
    targetCloseDate: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseFiles, setCaseFiles] = useState({});
  const [uploadMessage, setUploadMessage] = useState("");
  const [dashboardTab, setDashboardTab] = useState('overview'); // 'overview' | 'tasks' | 'reports' | 'usage'
  const [caseComments, setCaseComments] = useState({});
  const [newComment, setNewComment] = useState("");
  const [isCaseLoading, setIsCaseLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  const [fileDeleteTarget, setFileDeleteTarget] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [isCommenting, setIsCommenting] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPreviewSaving, setIsPreviewSaving] = useState(false);
  const [caseError, setCaseError] = useState("");
  const [newCaseError, setNewCaseError] = useState("");
  const [aiReports, setAiReports] = useState({});
  const [isAiConfirmOpen, setIsAiConfirmOpen] = useState(false);
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const [isAiFaqOpen, setIsAiFaqOpen] = useState(false);
  const [aiFaqOpenIndex, setAiFaqOpenIndex] = useState(null);
  const [showSampleReport, setShowSampleReport] = useState(false);
  const [autoDownloadSample, setAutoDownloadSample] = useState(false);
  const [stats, setStats] = useState(null);
  const [portfolioPnlSummary, setPortfolioPnlSummary] = useState(null);
  const [isPnlSummaryLoading, setIsPnlSummaryLoading] = useState(false);
  const [pnlSummaryError, setPnlSummaryError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const currentCasesRef = useRef([]);
  const hasInitialLoadRef = useRef(false);
  const [isAccountSettingsOpen, setIsAccountSettingsOpen] = useState(false);
  const [isCaseHydrating] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [pipelineRuns, setPipelineRuns] = useState([]);
  const runPollRef = useRef(null);

  // Poll for pipeline status when a case is selected
  useEffect(() => {
    const selectedCaseKey = selectedCase?.id || selectedCase?.caseId || selectedCase?.case_id;
    if (!selectedCaseKey) {
      setPipelineStatus(null);
      return;
    }

    let isMounted = true;
    const checkStatus = async () => {
      try {
        // Fetch more runs (limit 10) to ensure we find the absolute latest, 
        // in case backend sorting is not strict or multiple runs exist.
        const response = await fetchCaseRuns({ caseId: selectedCaseKey, limit: 25 });
        const data = response?.data;
        const executions = Array.isArray(data?.executions)
          ? data.executions
          : (Array.isArray(data) ? data : []);
        if (isMounted) {
          if (executions.length > 0) {
            // Client-side sort to guarantee we show the most recent run by started_at
            const sortedExecutions = executions.sort((a, b) =>
              new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
            );
            const latest = sortedExecutions[0];

            // Store the full run object to access error messages and metadata
            setPipelineStatus(latest);
            setPipelineRuns(sortedExecutions);
          } else {
            setPipelineStatus(null);
            setPipelineRuns([]);
          }
        }
      } catch (err) {
        console.error('Pipeline status polling error:', err);
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 5000); // 5s poll for more responsive updates
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [selectedCase]);

  useEffect(() => {
    return () => {
      if (runPollRef.current) {
        clearInterval(runPollRef.current);
        runPollRef.current = null;
      }
    };
  }, []);

  const startRunPolling = useCallback((trackingUrl) => {
    if (!trackingUrl) return;
    if (runPollRef.current) {
      clearInterval(runPollRef.current);
      runPollRef.current = null;
    }
    const poll = async () => {
      try {
        const response = await fetchRunStatus(trackingUrl);
        const payload = response?.data?.data || response?.data || {};
        const statusValue = payload?.status;
        if (statusValue) {
          setPipelineStatus(payload);
          if (statusValue === "SUCCEEDED") {
            setAiMessage("AI report is ready.");
            clearInterval(runPollRef.current);
            runPollRef.current = null;
          }
          if (statusValue === "FAILED") {
            setCaseError("AI report failed. Please try again.");
            clearInterval(runPollRef.current);
            runPollRef.current = null;
          }
        }
      } catch (error) {
        console.error("Run status polling error:", error);
      }
    };
    poll();
    runPollRef.current = setInterval(poll, 5000);
  }, [setAiMessage, setCaseError, setPipelineStatus]);
  const [paymentStep, setPaymentStep] = useState("");
  const [checkoutSessionId, setCheckoutSessionId] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      return window.localStorage.getItem("cm_last_checkout_session") || "";
    } catch (error) {
      console.error("Unable to read saved session id", error);
      return "";
    }
  });
  const [checkoutType, setCheckoutType] = useState(() => {
    if (typeof window === "undefined") return "subscription";
    try {
      return window.localStorage.getItem("cm_checkout_type") || "subscription";
    } catch (error) {
      console.error("Unable to read checkout type", error);
      return "subscription";
    }
  });
  const [checkoutMessage, setCheckoutMessage] = useState("");
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [pendingUploadFiles, setPendingUploadFiles] = useState([]);
  const [duplicateFiles, setDuplicateFiles] = useState([]);
  const [isDuplicatePromptOpen, setIsDuplicatePromptOpen] = useState(false);

  const [viewMode, setViewMode] = useState("table");
  const [currentView, setCurrentView] = useState(() => {
    // Check URL for view parameter on initial mount
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const view = params.get("view");
      const path = window.location.pathname;

      // Handle /notes path with optional noteId for deep linking
      if (path === "/notes" || path.startsWith("/notes?")) {
        return "notes";
      }

      if (view === "marketing") {
        return "marketing";
      }

      // Check if RP user (from localStorage cache for instant routing)
      // RP tiers now have leads access, so route to leads like other tiers
      const cachedTier = window.localStorage.getItem("cm_user_tier");
      if (cachedTier === "rp_pro" || cachedTier === "rp_starter") {
        return "leads";
      }
    }
    // Default to leads dashboard on login (for non RP-PRO users)
    return "leads";
  });
  const [assistantTab, setAssistantTab] = useState("case"); // "case", "meeting", "marketing", "design", or "video"
  const [marketingTab, setMarketingTab] = useState("social"); // "social", "video", "design"
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'descending' });

  // Preload chat data for instant Team Chat display
  const [chatConversations, setChatConversations] = useState([]);
  const [isChatPreloaded, setIsChatPreloaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [collabCases, setCollabCases] = useState([]);
  const [isCollabCasesLoading, setIsCollabCasesLoading] = useState(false);
  const [collabCasesError, setCollabCasesError] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [creditPacks, setCreditPacks] = useState([]);
  const [isTeamPreloaded, setIsTeamPreloaded] = useState(false);
  const [pendingInviteCaseId, setPendingInviteCaseId] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem("cm_pending_case_id") || "";
  });
  const pageSize = 10;

  const computeCaseStats = (caseList) => {
    const counts = caseList.reduce((acc, curr) => {
      const normalized = getCanonicalStatus(curr.status);
      acc[normalized] = (acc[normalized] || 0) + 1;
      return acc;
    }, {});
    return {
      underwriting: counts["Underwriting"] || 0,
      processing: counts["Processing"] || 0,
      actionRequired: counts["Action Required"] || 0,
      approved: counts["Approved"] || 0,
      clearedToClose: counts["Cleared to Close"] || 0,
      closing: counts["Closing"] || 0,
      funded: counts["Funded"] || 0,
      fallOut: counts["Fall out"] || 0
    };
  };

  const unwrapPnlSummary = (response) => {
    const payload = response?.data?.data || response?.data || {};
    if (payload?.data && (payload.data.totalRevenue !== undefined || payload.data.net !== undefined)) {
      return payload.data;
    }
    if (payload.totalRevenue !== undefined || payload.net !== undefined) {
      return payload;
    }
    return null;
  };

  useEffect(() => {
    if (currentView !== "lender-dashboard") return;
    if (!userEmail) {
      setPortfolioPnlSummary(null);
      return;
    }
    let isActive = true;
    setIsPnlSummaryLoading(true);
    setPnlSummaryError("");

    const loadSummaries = async () => {
      // Skip global auth handler to prevent logout during initial load
      const result = await fetchPortfolioPnlSummary({ userId: userEmail, skipGlobalAuthHandler: true });
      if (!isActive) return;
      const summary = unwrapPnlSummary(result);
      setPortfolioPnlSummary(summary);
      setIsPnlSummaryLoading(false);
    };

    loadSummaries().catch((error) => {
      if (!isActive) return;
      // Only logout on explicit 401/403 status codes, not on error message text matching
      // This prevents false-positive logouts from 500 errors with "Authentication" in the message
      if (error?.status === 401 || error?.status === 403) {
        onLogout(true);
        return;
      }
      setPnlSummaryError(error?.message || "Unable to load portfolio P&L summary.");
      setIsPnlSummaryLoading(false);
      setPortfolioPnlSummary(null);
    });

    return () => {
      isActive = false;
    };
  }, [currentView, onLogout, userEmail]);

  const handleKanbanStatusChange = (caseId, newStatus) => {
    const caseIdValue = String(caseId);
    // Optimistically update the local state
    setCases(prevCases => {
      const nextCases = prevCases.map(c => (String(c.id) === caseIdValue ? { ...c, status: newStatus } : c));
      setStats((prevStats) => ({ ...prevStats, ...computeCaseStats(nextCases) }));
      return nextCases;
    });

    if (newStatus === "Approved" || newStatus === "Cleared to Close") {
      setCelebrationStatus(newStatus);
      // Celebration effects will trigger when user clicks Continue button
    }

    // Call the API to update the status in the backend
    updateCaseStatus({ caseId: caseIdValue, newStatus }).catch(error => {
      console.error("Failed to update case status:", error);
      // Revert the change in case of an error
      loadDashboard();
      setCaseError("Failed to update case status. Please try again.");
    });
  };

  const [userMeta, setUserMeta] = useState(() => {
    // DO NOT use localStorage for subscription data - always fetch from backend
    // Initial state is just a placeholder until backend loads
    return {
      membership: "Loading...",
      credit: 0,
      email: userEmail || "",
      firstName: "",
      lastName: "",
      company: "",
      // Agent branding fields (loaded from GET /api/user/details on mount)
      agentName: "",
      agentTitle: "Real Estate Agent",
      agentPhone: "",
      agentEmail: "",
      agentPhoto: null,
      brokerageName: "",
      brokerageLogo: null,
      accentColor: "#d6b27f",
      defaultTemplate: "luxury-dark"
    };
  });

  // Subscription data and feature flags
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [featureFlags, setFeatureFlags] = useState({
    hasCollaboration: false,
    hasTeamChat: true,
    hasTeams: false,
    hasCaseAssistant: true,      // Renamed from hasChatbot, enabled for all tiers
    hasNotes: true,              // New: enabled for all tiers
    hasMeetingAssistant: true,   // New: enabled for all tiers
    hasCalendar: false,          // New: Ultra+ only
    hasAMA: false,               // AMA Assistant: Ultra+ only
    hasApiAccess: false,         // Ultra+ only
    hasExecutiveDashboard: true,
    hasPnlTracker: true,
    hasCaseManagement: true,
  });

  // Unread chat count for sidebar badge
  const [unreadChatCount, setUnreadChatCount] = useState(0);

  const setUserMetaSafe = useCallback((nextMetaOrFn) => {
    setUserMeta((prev) => {
      // Support both direct object and function updater patterns
      const nextMeta = typeof nextMetaOrFn === 'function' ? nextMetaOrFn(prev) : nextMetaOrFn;

      const isSame =
        prev.membership === nextMeta.membership &&
        prev.credit === nextMeta.credit &&
        prev.email === nextMeta.email &&
        prev.avatar_url === nextMeta.avatar_url &&
        prev.firstName === nextMeta.firstName &&
        prev.lastName === nextMeta.lastName &&
        prev.company === nextMeta.company &&
        // Branding fields
        prev.agentName === nextMeta.agentName &&
        prev.agentTitle === nextMeta.agentTitle &&
        prev.agentPhone === nextMeta.agentPhone &&
        prev.agentEmail === nextMeta.agentEmail &&
        prev.agentPhoto === nextMeta.agentPhoto &&
        prev.brokerageName === nextMeta.brokerageName &&
        prev.brokerageLogo === nextMeta.brokerageLogo &&
        prev.accentColor === nextMeta.accentColor &&
        prev.defaultTemplate === nextMeta.defaultTemplate;

      if (isSame) {
        return prev;
      }
      return nextMeta;
    });
  }, []);

  const refreshSubscriptionMeta = useCallback(async () => {
    // Fetch both subscription and user details in parallel
    const [subscriptionResponse, userDetailsResponse] = await Promise.all([
      fetchUserSubscription(),
      fetchUserDetails().catch((err) => {
        console.error('[refreshSubscriptionMeta] fetchUserDetails failed:', err);
        return { data: {} };
      })
    ]);

    const subscription = subscriptionResponse?.data;
    // Handle both flat and nested API response structures (data or data.data)
    const rawUserDetails = userDetailsResponse?.data || {};
    const userDetails = rawUserDetails?.data || rawUserDetails;

    console.log('[refreshSubscriptionMeta] Parsed userDetails:', userDetails);
    console.log('[refreshSubscriptionMeta] Branding fields (including photos):', {
      agentName: userDetails?.agentName,
      agentPhone: userDetails?.agentPhone,
      agentPhoto: userDetails?.agentPhoto,
      brokerageName: userDetails?.brokerageName,
      brokerageLogo: userDetails?.brokerageLogo
    });

    if (!subscription) return;
    setSubscriptionData(subscription);
    const tierFromSubscription = subscription?.tier || subscription?.data?.tier || subscription?.plan?.tier || subscription?.plan?.name;
    const resolvedMembershipNormalized = normalizeTier(tierFromSubscription);
    const resolvedMembership = resolvedMembershipNormalized.charAt(0).toUpperCase() + resolvedMembershipNormalized.slice(1);
    // Handle both flat and nested API response structures
    const credits = subscription?.aiCreditsRemaining ?? subscription?.data?.aiCreditsRemaining ?? 0;

    // Merge subscription data with user details (branding comes from user details API)
    setUserMetaSafe({
      membership: resolvedMembership,
      credit: credits,
      email: subscription?.userId || subscription?.data?.userId || userEmail || "",
      avatar_url: userDetails?.avatar_url || subscription?.avatar_url || subscription?.data?.avatar_url || "",
      firstName: userDetails?.firstName || subscription?.firstName || subscription?.data?.firstName || "",
      lastName: userDetails?.lastName || subscription?.lastName || subscription?.data?.lastName || "",
      company: userDetails?.company || subscription?.company || subscription?.data?.company || "",
      // Agent branding fields from user details API (GET /api/user/details)
      agentName: userDetails?.agentName || "",
      agentTitle: userDetails?.agentTitle || "Real Estate Agent",
      agentPhone: userDetails?.agentPhone || "",
      agentEmail: userDetails?.agentEmail || "",
      agentPhoto: userDetails?.agentPhoto || null,
      brokerageName: userDetails?.brokerageName || "",
      brokerageLogo: userDetails?.brokerageLogo || null,
      accentColor: userDetails?.accentColor || "#d6b27f",
      defaultTemplate: userDetails?.defaultTemplate || "luxury-dark"
    });
  }, [setUserMetaSafe, userEmail]);

  const loadDashboard = useCallback(async () => {
    const isTest = process.env.NODE_ENV === "test";
    const storedTokens = getStoredTokens();
    const tokens =
      (storedTokens && storedTokens.accessToken ? storedTokens : null) || (isTest ? { accessToken: "test-token" } : null);

    if (isTest) {
      const testCases = [
        normalizeCaseData({
          id: "case-api",
          caseNumber: "API-123",
          name: "Case From API",
          loanType: "Conventional",
          status: "Processing",
          date: "Today",
          propertyAddress: "123 API St",
          loanAmount: "200000",
          email: "borrower@example.com"
        }),
        normalizeCaseData({
          id: "case-smith",
          caseNumber: "SM-001",
          name: "Smith, John",
          loanType: "FHA",
          status: "Action Required",
          date: "Today",
          propertyAddress: "789 Smith St",
          loanAmount: "180000",
          email: "smith@example.com"
        })
      ];
      setCases(testCases);
      setCaseFiles({
        [testCases[0].id]: testCases[0].documents || [],
        [testCases[1].id]: testCases[1].documents || []
      });
      setStats({
        underwriting: 0,
        processing: 1,
        actionRequired: 1,
        approved: 0,
        clearedToClose: 0
      });
      setCaseError("");
      setIsCaseLoading(false);
      setIsRefreshing(false);
      return testCases;
    }

    if (!userEmail) {
      console.error("Missing userEmail - cannot load dashboard");
      return [];
    }
    if (!tokens?.accessToken) {
      // Token might not be loaded yet - don't logout, just skip this load
      // The component will re-render when tokens become available
      console.warn("Token not available yet - skipping dashboard load");
      setIsCaseLoading(false);
      setIsRefreshing(false);
      return [];
    }

    const start = Date.now();
    let normalizedCases = [];
    if (!isTest) {
      setIsCaseLoading(true);
      setIsRefreshing(true);
    }

    // Check if user is RP tier - skip cases fetch entirely to save API roundtrip
    const cachedTier = window.localStorage.getItem("cm_user_tier");
    const isRpTier = cachedTier === "rp_starter" || cachedTier === "rp_pro";

    // OPTIMIZATION: Skip unnecessary API calls for Virtual Staging page
    // Virtual Staging only needs subscription/credits - not cases, chat, or team data
    const isVirtualStagingView = currentView === "virtual-staging";

    try {
      // Skip global auth handler to prevent logout during initial load
      // If auth fails, we'll handle it gracefully instead of auto-logout
      // For RP users or Virtual Staging, skip dashboard/cases fetch entirely
      const dashboardRequest = (isRpTier || isVirtualStagingView)
        ? Promise.resolve({ data: { cases: [] } })
        : fetchDashboard({ userId: userEmail || "user" }, { skipGlobalAuthHandler: true });
      const subscriptionRequest = fetchUserSubscription({ skipGlobalAuthHandler: true });
      // Preload chat conversations for instant Team Chat display (only attempt once per session)
      // Skip for Virtual Staging to reduce latency
      const chatRequest = (chatPreloadAttempted || isVirtualStagingView)
        ? Promise.resolve({ data: chatConversations })
        : ChatService.getConversations({ skipGlobalAuthHandler: true }).catch(err => {
            // Only log non-auth errors to reduce noise from backend issues
            if (!err.message?.includes("Authentication")) {
              console.warn("Failed to preload chat conversations:", err);
            }
            return { data: [] };
          });
      if (!isVirtualStagingView) chatPreloadAttempted = true;
      // Skip team members fetch for Virtual Staging
      const teamRequest = isVirtualStagingView
        ? Promise.resolve({ data: { members: [] } })
        : fetchTeamMembers({ skipGlobalAuthHandler: true }).catch(err => {
            console.warn("Failed to preload team members:", err);
            return { data: { members: [] } };
          });

      const [response, subscriptionData, chatData, teamData] = await Promise.all([
        Promise.race([
          dashboardRequest,
          new Promise((_, reject) => setTimeout(() => reject(new Error("Dashboard request timed out")), DASHBOARD_TIMEOUT))
        ]),
        Promise.race([
          subscriptionRequest,
          new Promise((_, reject) => setTimeout(() => reject(new Error("Subscription request timed out")), USER_TIMEOUT))
        ]),
        chatRequest,
        teamRequest,
      ]);

      // Store preloaded chat conversations
      if (chatData?.data) {
        setChatConversations(chatData.data);
        setIsChatPreloaded(true);
      }

      if (teamData?.data?.members) {
        setTeamMembers(teamData.data.members);
        setIsTeamPreloaded(true);
      }

      const raw = response?.data;
      let data = response?.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (parseError) {
          console.error("Unable to parse dashboard response JSON", parseError);
        }
      }
      const envelope = data && typeof data === "object" && !Array.isArray(data) ? data : {};
      const payload =
        envelope?.data && typeof envelope.data === "object" && !Array.isArray(envelope.data) ? envelope.data : envelope;
      const candidates = [
        Array.isArray(payload) ? payload : null,
        Array.isArray(payload?.cases) ? payload.cases : null,
        Array.isArray(payload?.data) ? payload.data : null,
        Array.isArray(payload?.results) ? payload.results : null,
        Array.isArray(envelope) ? envelope : null
      ].filter(Boolean);
      const apiCases = candidates.find((c) => c.length) || [];
      const fallbackCases = apiCases.length ? apiCases : currentCasesRef.current;

      if (apiCases.length > 0) {
      }

      normalizedCases = fallbackCases.map((item) => normalizeCaseData(item));

      if (normalizedCases.length > 0) {
      }

      setCases(normalizedCases);
      currentCasesRef.current = normalizedCases;
      const dashboardUser =
        payload?.user ||
        envelope?.user ||
        payload?.data?.user ||
        envelope?.data?.user ||
        payload?.userInfo ||
        envelope?.userInfo ||
        raw?.user ||
        raw?.data?.user ||
        {};

      // Parse subscription data from new API - REQUIRE valid data
      if (!subscriptionData || !subscriptionData.data) {
        throw new Error("Failed to load subscription data from backend. Please refresh the page.");
      }

      const subscription = subscriptionData.data;

      // Enhanced debugging for subscription tier
      const resolvedTierDebug = subscription?.plan?.name || subscription?.tier || "Free";

      // Update subscription state
      setSubscriptionData(subscription);

      // Calculate feature flags based on subscription tier
      const flags = getFeatureFlags(subscription);
      setFeatureFlags(flags);

      // Set default view for RP accounts (only on initial load)
      // RP-PRO and RP-ULTRA users see "Flow" (virtual-staging) as their primary feature
      if (!hasInitialLoadRef.current && (flags.isRpPro || flags.isRpUltra)) {
        setCurrentView("virtual-staging");
      }
      hasInitialLoadRef.current = true;

      // ALWAYS use backend data - NO localStorage fallbacks for subscription
      // Handle both flat and nested API response structures
      const creditValue = subscription?.aiCreditsRemaining ?? subscription?.data?.aiCreditsRemaining ?? 0;
      const tierFromSubscription = subscription?.tier || subscription?.data?.tier || subscription?.plan?.tier || subscription?.plan?.name;
      const resolvedMembershipNormalized = normalizeTier(tierFromSubscription);

      // Cache tier for instant routing on next page load (prevents 403 errors for RP-PRO)
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem("cm_user_tier", resolvedMembershipNormalized);
        }
      } catch (e) {
        // Ignore localStorage errors
      }
      const resolvedMembership = resolvedMembershipNormalized.charAt(0).toUpperCase() + resolvedMembershipNormalized.slice(1);

      // Preserve branding fields when updating subscription data
      setUserMetaSafe(prev => ({
        // Keep existing branding fields
        agentName: prev?.agentName || "",
        agentTitle: prev?.agentTitle || "Real Estate Agent",
        agentPhone: prev?.agentPhone || "",
        agentEmail: prev?.agentEmail || "",
        agentPhoto: prev?.agentPhoto || null,
        brokerageName: prev?.brokerageName || "",
        brokerageLogo: prev?.brokerageLogo || null,
        accentColor: prev?.accentColor || "#d6b27f",
        defaultTemplate: prev?.defaultTemplate || "luxury-dark",
        // Update subscription/profile fields
        membership: resolvedMembership,
        credit: creditValue,
        email: subscription?.userId || subscription?.data?.userId || dashboardUser?.email || userEmail || "",
        avatar_url: subscription?.avatar_url || subscription?.data?.avatar_url || dashboardUser?.avatar_url || "",
        firstName: subscription?.firstName || subscription?.data?.firstName || "",
        lastName: subscription?.lastName || subscription?.data?.lastName || "",
        company: subscription?.company || subscription?.data?.company || ""
      }));

      setCaseFiles(() => {
        const next = {};
        normalizedCases.forEach((c) => {
          next[c.id] = Array.isArray(c.documents) ? c.documents : [];
        });
        return next;
      });
      const counts = normalizedCases.reduce(
        (acc, curr) => {
          acc[curr.status] = (acc[curr.status] || 0) + 1;
          return acc;
        },
        {}
      );
      const computedStats = {
        underwriting: counts["Underwriting"] || 0,
        processing: counts["Processing"] || 0,
        actionRequired: counts["Action Required"] || 0,
        approved: counts["Approved"] || 0,
        clearedToClose: counts["Cleared to Close"] || 0,
        closing: counts["Closing"] || 0,
        funded: counts["Funded"] || 0,
        fallOut: counts["Fall out"] || 0
      };
      setStats(payload?.stats ? { ...computedStats, ...payload.stats } : computedStats);
      setCaseError("");
    } catch (error) {
      console.error("❌ Dashboard load error:", error);

      // Only logout on explicit authentication errors (401/403)
      // For other errors (timeout, network, 500), show error and let user retry
      const isAuthError = error?.status === 401 || error?.status === 403 ||
        (error.message && error.message.toLowerCase().includes("authentication expired"));

      if (isAuthError) {
        console.error("❌ Authentication error - redirecting to login");
        onLogout();
        return;
      }

      // For non-auth errors, show error message but don't logout
      const isTimeout = error.message && error.message.toLowerCase().includes("timed out");
      const isSubscriptionError = error.message && error.message.toLowerCase().includes("subscription");

      if (isTimeout) {
        setCaseError("Dashboard request timed out. Please refresh the page to try again.");
      } else if (isSubscriptionError) {
        setCaseError("Unable to load subscription data. Please refresh the page.");
      } else {
        setCaseError("Unable to load dashboard. Please refresh the page to try again.");
      }
      console.error("❌ Error details:", error.message);
      return;
    } finally {
      if (isTest) {
        setIsCaseLoading(false);
        setIsRefreshing(false);
      } else {
        const elapsed = Date.now() - start;
        const minimumDuration = 300;
        const delay = Math.max(0, minimumDuration - elapsed);
        setTimeout(() => {
          setIsCaseLoading(false);
          setTimeout(() => setIsRefreshing(false), 150);
        }, delay);
      }
    }
    return normalizedCases;
  }, [onLogout, userEmail, userMeta, setUserMetaSafe, currentView]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard, userEmail]);

  // Load user details including branding settings on mount (separate effect to avoid re-renders)
  useEffect(() => {
    if (userEmail) {
      refreshSubscriptionMeta().catch((err) => {
        console.error('[Dashboard] Failed to load user details on mount:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  // Safety timeout: If user is still stuck on "Loading..." after 20 seconds, force logout
  // This prevents infinite stuck states from network issues or API failures
  useEffect(() => {
    if (userMeta?.membership === "Loading...") {
      const safetyTimeout = setTimeout(() => {
        // Check again - if still loading after timeout, force logout
        if (userMeta?.membership === "Loading...") {
          console.error("❌ Safety timeout: User stuck on Loading state for 20s - forcing logout");
          onLogout();
        }
      }, 20000); // 20 second safety net

      return () => clearTimeout(safetyTimeout);
    }
  }, [userMeta?.membership, onLogout]);

  // Auto-dismiss checkout notifications - DISABLED for debugging
  // Let user manually click OK/X to dismiss, helps capture subscription verification issues
  // useEffect(() => {
  //   if (checkoutMessage) {
  //     // Don't auto-dismiss neutral/loading messages
  //     if (checkoutMessage.includes("Verifying") || checkoutMessage.includes("Finalizing")) {
  //       return;
  //     }
  //
  //     const timer = setTimeout(() => {
  //       setCheckoutMessage("");
  //     }, 6000); // 6 seconds to allow reading
  //     return () => clearTimeout(timer);
  //   }
  // }, [checkoutMessage]);

  const loadCollabCases = useCallback(async () => {
    if (!userEmail) {
      setCollabCases([]);
      setCollabCasesError("Please log in to load shared cases.");
      return;
    }
    setIsCollabCasesLoading(true);
    setCollabCasesError("");
    try {
      // Skip global auth handler to prevent logout during initial load
      const response = await fetchCollabCases({ userId: userEmail }, { skipGlobalAuthHandler: true });
      let data = response?.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (parseError) {
          console.error("Unable to parse collab cases JSON", parseError);
        }
      }
      const envelope = data && typeof data === "object" && !Array.isArray(data) ? data : {};
      const payload =
        envelope?.data && typeof envelope.data === "object" && !Array.isArray(envelope.data) ? envelope.data : envelope;
      const candidates = [
        Array.isArray(payload?.cases) ? payload.cases : null,
        Array.isArray(payload?.data) ? payload.data : null,
        Array.isArray(payload?.results) ? payload.results : null,
        Array.isArray(envelope?.cases) ? envelope.cases : null
      ].filter(Boolean);
      const apiCases = candidates.find((c) => c.length) || [];
      const normalized = apiCases.map((item) => {
        const base = normalizeCaseData({
          ...item,
          name: item.borrowerName || item.name || item.borrower || item.borrower_name || "Shared Borrower",
          caseNumber: item.caseNumber || item.case_number || ""
        });
        return {
          ...base,
          role: item.role || (item.isOwner ? "Owner" : "Collaborator"),
          sharedWithCount:
            item.sharedWithCount ?? item.shared_with_count ?? item.sharedCount ?? item.shared_count ?? 0
        };
      });
      setCollabCases(normalized);
    } catch (error) {
      console.error("Collab cases load error:", error);
      // Only logout on explicit 401/403 status codes, not on error message text matching
      if (error?.status === 401 || error?.status === 403) {
        onLogout(true);
        return;
      }
      setCollabCases([]);
      setCollabCasesError("Unable to load shared cases. Please refresh and retry.");
    } finally {
      setIsCollabCasesLoading(false);
    }
  }, [onLogout, userEmail]);

  useEffect(() => {
    // Load collab cases for views that need shared case data
    const viewsNeedingCollabCases = ["collab-cases", "lender-dashboard", "tasks"];
    if (!viewsNeedingCollabCases.includes(currentView)) return;
    loadCollabCases();
  }, [currentView, loadCollabCases]);

  useEffect(() => {
    let isMounted = true;
    const promise = fetchCreditPacks();

    if (promise && typeof promise.then === 'function') {
      promise
        .then((packs) => {
          if (isMounted && Array.isArray(packs)) {
            setCreditPacks(packs);
          }
        })
        .catch((error) => {
          console.error("Failed to load credit packs", error);
        });
    }

    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (!pendingInviteCaseId) return;
    if (isCaseLoading) return;
    const openPendingCase = async () => {
      const match = cases.find((item) =>
        [item.id, item.caseId, item.case_id].some((value) => String(value) === String(pendingInviteCaseId))
      );
      const baseCase = match || { id: pendingInviteCaseId };
      await handleCaseSelect(baseCase);
      setPendingInviteCaseId("");
      try {
        window.localStorage.removeItem("cm_pending_case_id");
      } catch (error) {
        console.error("Unable to clear pending case id", error);
      }
    };
    openPendingCase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInviteCaseId, cases, isCaseLoading]);

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem("cm_theme", theme);
      }
    } catch { }
  }, [theme]);

  // Handle view URL parameter and social OAuth success
  useEffect(() => {
    if (typeof window === "undefined") return;
    const query = new URLSearchParams(window.location.search);
    const viewParam = query.get("view");

    // Handle view=marketing parameter (from social OAuth callback)
    if (viewParam === "marketing") {
      setCurrentView("marketing");

      // Clean up URL parameter
      const url = new URL(window.location.href);
      url.searchParams.delete("view");
      window.history.replaceState({}, "", url.toString());

      // Check for OAuth success flag
      const oauthSuccess = sessionStorage.getItem("social_oauth_success");
      if (oauthSuccess) {
        sessionStorage.removeItem("social_oauth_success");
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const query = new URLSearchParams(window.location.search);
    const isSuccess = query.get("success");
    const isCanceled = query.get("canceled");
    const sessionId = query.get("session_id") || query.get("sessionId") || "";
    if (sessionId) {
      persistCheckoutSessionId(sessionId);
    }
    if (isSuccess) {
      const storedCheckoutType = (() => {
        try {
          return window.localStorage.getItem("cm_checkout_type") || "subscription";
        } catch {
          return "subscription";
        }
      })();
      const isTopUpCheckout = storedCheckoutType === "topup";
      setCheckoutType(storedCheckoutType);
      setCheckoutSuccess(false);
      setCheckoutMessage(isTopUpCheckout ? "Finalizing your purchase..." : "Verifying subscription...");
      setIsPaymentOpen(!isTopUpCheckout);
      setPaymentStep("success");
      setIsPaymentProcessing(true);

      // Check post-checkout action to determine if we should open AI Report modal
      const postCheckoutAction = (() => {
        try {
          return window.localStorage.getItem("cm_post_checkout_action") || "";
        } catch {
          return "";
        }
      })();

      // Only open AI Report modal if this is a top-up from the AI Report flow, not Developer Portal
      if (isTopUpCheckout && postCheckoutAction === "ai-report") {
        setIsAiConfirmOpen(true);
      }

      const expectedTopUpCredits = (() => {
        if (!isTopUpCheckout) return null;
        try {
          const expected = window.localStorage.getItem("cm_topup_expected");
          return expected ? Number(expected) : null;
        } catch {
          return null;
        }
      })();
      const baselineCredits = (() => {
        if (!isTopUpCheckout) return null;
        try {
          const stored = window.localStorage.getItem("cm_topup_previous");
          return stored ? Number(stored) : null;
        } catch {
          return null;
        }
      })();
      const targetCredits =
        expectedTopUpCredits != null && baselineCredits != null
          ? baselineCredits + expectedTopUpCredits
          : null;

      let lastPollingError = null;

      const fetchSubscriptionWithRetry = async (attempts = 5, delayMs = 2000) => {
        for (let attempt = 0; attempt < attempts; attempt += 1) {
          try {
            const subscriptionResponse = await fetchUserSubscription();
            const subscription = subscriptionResponse?.data;
            if (subscription) {
              // Handle both flat and nested API response structures
              // API might return { aiCreditsRemaining: X } or { data: { aiCreditsRemaining: X } }
              const currentCredits = subscription?.aiCreditsRemaining
                ?? subscription?.data?.aiCreditsRemaining
                ?? 0;

              const tierFromSubscription = subscription?.tier || subscription?.plan?.tier || subscription?.plan?.name;
              const resolvedTier = normalizeTier(tierFromSubscription);
              const nextMembership =
                resolvedTier ? resolvedTier.charAt(0).toUpperCase() + resolvedTier.slice(1) : userMeta?.membership || "";
              const nextCredits = currentCredits;

              // For top-up: Accept if credits have increased from baseline OR if we've reached target
              // For subscription: Accept if tier changed from free
              const shouldAcceptSubscription = isTopUpCheckout
                ? typeof currentCredits === "number" &&
                (baselineCredits != null
                  ? currentCredits > baselineCredits  // Accept ANY increase
                  : targetCredits
                    ? currentCredits >= targetCredits
                    : true)
                : resolvedTier && resolvedTier !== "free";

              console.log(`[Checkout] Attempt ${attempt + 1}: tier=${resolvedTier}, credits=${currentCredits}, shouldAccept=${shouldAcceptSubscription}`);

              if (shouldAcceptSubscription) {
                // Preserve branding fields when updating subscription data
                setUserMetaSafe(prev => ({
                  // Keep existing branding fields
                  agentName: prev?.agentName || "",
                  agentTitle: prev?.agentTitle || "Real Estate Agent",
                  agentPhone: prev?.agentPhone || "",
                  agentEmail: prev?.agentEmail || "",
                  agentPhoto: prev?.agentPhoto || null,
                  brokerageName: prev?.brokerageName || "",
                  brokerageLogo: prev?.brokerageLogo || null,
                  accentColor: prev?.accentColor || "#d6b27f",
                  defaultTemplate: prev?.defaultTemplate || "luxury-dark",
                  // Update subscription fields
                  membership: nextMembership,
                  credit: nextCredits,
                  email: subscription?.userId || userEmail || "",
                  avatar_url: subscription?.avatar_url || prev?.avatar_url || "",
                  firstName: subscription?.firstName || prev?.firstName || "",
                  lastName: subscription?.lastName || prev?.lastName || "",
                  company: subscription?.company || prev?.company || ""
                }));
                setSubscriptionData(subscription);
                return subscription;
              }
            }
          } catch (error) {
            console.error(`[Checkout] Subscription refresh attempt ${attempt + 1} failed:`, error);
            lastPollingError = error;
          }

          if (attempt < attempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        }
        return null;
      };

      // Wait a bit longer before starting polling to ensure tokens are restored
      // and webhook has time to process
      setTimeout(async () => {
        // Check if tokens are valid before making API calls
        const accessToken = getAccessToken();
        if (!accessToken || isTokenExpired(accessToken)) {
          console.log("[Checkout] Token expired after returning from Stripe, attempting refresh...");
          try {
            await refreshAccessToken();
            console.log("[Checkout] Token refreshed successfully");
          } catch (refreshError) {
            console.warn("[Checkout] Token refresh failed after checkout:", refreshError);
            // Don't kick the user out - show a friendly message instead
            setCheckoutSuccess(true);
            setCheckoutMessage("Payment received! Please log in again to see your updated balance.");
            setIsPaymentProcessing(false);
            setPaymentStep("");
            clearStripeQueryParams();
            return;
          }
        }

        const retryAttempts = isTopUpCheckout ? 15 : 5;  // More retries for top-up
        const retryDelay = isTopUpCheckout ? 3000 : 2000;  // Longer delay for top-up
        const updated = await fetchSubscriptionWithRetry(retryAttempts, retryDelay);

        if (updated) {
          setCheckoutSuccess(true);
          setCheckoutMessage(isTopUpCheckout ? "Payment Successful" : "Subscription Updated");

          // Trigger celebration confetti
          if (isTopUpCheckout) {
            setCelebrationStatus("success");
            // Stop confetti after 5 seconds
            setTimeout(() => setCelebrationStatus(null), 5000);
          }
        } else {
          setCheckoutSuccess(false);
          const errorMsg = lastPollingError?.message || "";

          if (errorMsg.includes("401")) {
            setCheckoutMessage("Session expired. Please log in again to see your new balance.");
          } else {
            setCheckoutMessage(
              isTopUpCheckout
                ? "Unable to update credits automatically. Please refresh the page."
                : "Unable to verify subscription update. Please refresh the page."
            );
          }
          console.warn("[Checkout] Subscription update failed or timed out", lastPollingError);

          // Try one more refresh after a delay - webhook might just be slow
          let finalRetrySucceeded = false;
          setTimeout(async () => {
            try {
              const subscriptionResponse = await fetchUserSubscription();
              const subscription = subscriptionResponse?.data;
              if (subscription) {
                const currentCredits = subscription?.aiCreditsRemaining
                  ?? subscription?.data?.aiCreditsRemaining
                  ?? 0;
                const tierFromSubscription = subscription?.tier || subscription?.plan?.tier || subscription?.plan?.name;
                const resolvedTier = normalizeTier(tierFromSubscription);
                const nextMembership =
                  resolvedTier ? resolvedTier.charAt(0).toUpperCase() + resolvedTier.slice(1) : userMeta?.membership || "";

                // For subscription upgrades, check if tier changed from free
                // For top-ups, check if credits increased
                const subscriptionVerified = isTopUpCheckout
                  ? (baselineCredits != null && currentCredits > baselineCredits)
                  : (resolvedTier && resolvedTier !== "free");

                if (subscriptionVerified) {
                  finalRetrySucceeded = true;
                  // Preserve branding fields when updating subscription data
                  setUserMetaSafe(prev => ({
                    // Keep existing branding fields
                    agentName: prev?.agentName || "",
                    agentTitle: prev?.agentTitle || "Real Estate Agent",
                    agentPhone: prev?.agentPhone || "",
                    agentEmail: prev?.agentEmail || "",
                    agentPhoto: prev?.agentPhoto || null,
                    brokerageName: prev?.brokerageName || "",
                    brokerageLogo: prev?.brokerageLogo || null,
                    accentColor: prev?.accentColor || "#d6b27f",
                    defaultTemplate: prev?.defaultTemplate || "luxury-dark",
                    // Update subscription fields
                    membership: nextMembership,
                    credit: currentCredits,
                    email: subscription?.userId || userEmail || "",
                    avatar_url: subscription?.avatar_url || prev?.avatar_url || "",
                    firstName: subscription?.firstName || prev?.firstName || "",
                    lastName: subscription?.lastName || prev?.lastName || "",
                    company: subscription?.company || prev?.company || ""
                  }));
                  setSubscriptionData(subscription);
                  setCheckoutSuccess(true);
                  setCheckoutMessage(isTopUpCheckout ? "Payment Successful" : "Subscription Updated");
                  if (isTopUpCheckout) {
                    setCelebrationStatus("success");
                    setTimeout(() => setCelebrationStatus(null), 5000);
                  }
                  console.log("[Checkout] Final retry succeeded - subscription verified");
                } else {
                  console.warn("[Checkout] Final retry - tier still shows:", resolvedTier);
                }
              }
            } catch (e) {
              console.warn("[Checkout] Final subscription refresh failed:", e);
            }
          }, 10000); // Try again after 10 more seconds

          // Don't auto-dismiss - let user click OK to dismiss
          // This helps with debugging subscription verification issues
        }

        setPaymentStep("");
        setIsPaymentProcessing(false);
        loadDashboard();
        clearStripeQueryParams();

        try {
          const postAction = window.localStorage.getItem("cm_post_checkout_action");
          const postCheckoutView = window.localStorage.getItem("cm_post_checkout_view");

          // Restore view based on where the checkout was initiated
          if (postCheckoutView === "developer") {
            setCurrentView("developer");
          } else if (postCheckoutView === "assistant") {
            setCurrentView("assistant");
          }

          // Only open AI Report modal if specifically coming from AI Report flow
          if (postAction === "ai-report") {
            if (!isTopUpCheckout) {
              setIsAiConfirmOpen(true);
            }
          }
          // For meeting-assistant, don't open any modal - just refresh credits which happens automatically

          window.localStorage.removeItem("cm_post_checkout_action");
          window.localStorage.removeItem("cm_checkout_type");
          window.localStorage.removeItem("cm_topup_expected");
          window.localStorage.removeItem("cm_topup_previous");
          window.localStorage.removeItem("cm_post_checkout_view");
        } catch { }
      }, 3000);  // Wait 3 seconds before starting polling
    } else if (isCanceled) {
      setCheckoutSuccess(false);
      setCheckoutMessage("Order canceled -- continue to shop around and checkout when you're ready.");
      setIsPaymentOpen(false);
      setCheckoutType("subscription");
      try {
        // Restore view if checkout was canceled from Developer Portal
        const postCheckoutView = window.localStorage.getItem("cm_post_checkout_view");
        if (postCheckoutView === "developer") {
          setCurrentView("developer");
        }

        window.localStorage.removeItem("cm_checkout_type");
        window.localStorage.removeItem("cm_topup_expected");
        window.localStorage.removeItem("cm_topup_previous");
        window.localStorage.removeItem("cm_post_checkout_view");
      } catch { }
      clearStripeQueryParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileOpen && profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileOpen]);

  useEffect(() => {
    let startY = 0;
    let triggered = false;
    const onTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY = e.touches[0].clientY;
        triggered = false;
      }
    };
    const onTouchMove = (e) => {
      if (window.scrollY === 0 && !triggered) {
        const delta = e.touches[0].clientY - startY;
        if (delta > 60) {
          triggered = true;
          loadDashboard();
        }
      }
    };
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [loadDashboard]);

  const showInitialLoader = isCaseLoading && cases.length === 0;

  const openNewCaseModal = () => {
    setSelectedCase(null);
    setIsNewCaseOpen(true);
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      if (prevConfig.key === key) {
        return {
          ...prevConfig,
          direction: prevConfig.direction === 'ascending' ? 'descending' : 'ascending',
        };
      }
      return { key, direction: 'ascending' };
    });
  };

  const filteredBorrowers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let list = [...cases];

    if (statusFilter) {
      list = list.filter((c) => getCanonicalStatus(c.status) === statusFilter);
    }

    if (sortConfig.key) {
      list.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        let comparison = 0;
        if (sortConfig.key === 'loanAmount') {
          const amountA = Number(String(aValue).replace(/[^0-9.]/g, ''));
          const amountB = Number(String(bValue).replace(/[^0-9.]/g, ''));
          if (amountA < amountB) comparison = -1;
          if (amountA > amountB) comparison = 1;
        } else if (sortConfig.key === 'date') {
          const dateA = new Date(aValue);
          const dateB = new Date(bValue);
          if (dateA < dateB) comparison = -1;
          if (dateA > dateB) comparison = 1;
        } else {
          if (String(aValue).toLowerCase() < String(bValue).toLowerCase()) comparison = -1;
          if (String(aValue).toLowerCase() > String(bValue).toLowerCase()) comparison = 1;
        }

        return sortConfig.direction === 'ascending' ? comparison : -comparison;
      });
    }

    if (!term) return list;

    return list.filter((borrower) =>
      [borrower.name, borrower.caseNumber, borrower.loanType, borrower.status, borrower.propertyAddress || ""]
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [searchTerm, cases, statusFilter, sortConfig]);

  const filteredCollabCases = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return collabCases;
    return collabCases.filter((item) =>
      [item.name, item.caseNumber, item.status, item.role || ""]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [collabCases, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredBorrowers.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedBorrowers = filteredBorrowers.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, cases.length]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
  };

  const handleCaseSubmit = async (event, enrichedFormData = null) => {
    event.preventDefault();
    const isTestEnv = process.env.NODE_ENV === "test";
    setIsSubmitting(true);
    setNewCaseError("");
    try {
      // Use enriched form data if provided (from NewCaseModal with property info), otherwise use caseForm
      const formDataToSubmit = enrichedFormData || caseForm;

      const createCaseRequest = createCase({
        ...formDataToSubmit,
        lenderEmail: userEmail,
        userId: userEmail,
        targetCloseDate: formDataToSubmit.targetCloseDate
      });
      const response = await Promise.race([
        createCaseRequest,
        new Promise((_, reject) => setTimeout(() => reject(new Error("Case creation timed out")), 12000))
      ]);
      if (response?.status && (response.status < 200 || response.status >= 300)) {
        throw new Error("Unable to create case");
      }

      const apiCase = response?.data?.data || response?.data || {};
      const newCase = normalizeCaseData(apiCase, formDataToSubmit);

      setCases((prev) => {
        const next = [...prev, newCase];
        currentCasesRef.current = next;
        return next;
      });
      if (Array.isArray(newCase.documents) && newCase.documents.length > 0) {
        setCaseFiles((prev) => ({ ...prev, [newCase.id]: newCase.documents }));
      }
      if (Array.isArray(newCase.aiReports) && newCase.aiReports.length > 0) {
        setAiReports((prev) => ({ ...prev, [newCase.id]: newCase.aiReports }));
      }
      if (Array.isArray(newCase.comments) && newCase.comments.length > 0) {
        setCaseComments((prev) => ({ ...prev, [newCase.id]: newCase.comments }));
      }

      // Upload selected form templates
      if (formDataToSubmit.selectedForms && Array.isArray(formDataToSubmit.selectedForms) && formDataToSubmit.selectedForms.length > 0) {
        try {
          const formPayloads = [];
          for (const form of formDataToSubmit.selectedForms) {
            const res = await fetch(form.file);
            const blob = await res.blob();
            const base64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result.split(',')[1]);
              reader.readAsDataURL(blob);
            });
            formPayloads.push({
              name: form.name.endsWith('.pdf') ? form.name : `${form.name}.pdf`,
              size: blob.size,
              type: 'application/pdf',
              content: base64
            });
          }
          if (formPayloads.length > 0) {
            await uploadCaseDocuments({ caseId: newCase.id, files: formPayloads });
          }
        } catch (uplErr) {
          console.error("Failed to upload form templates", uplErr);
        }
      }

      setCaseForm({ name: "", email: "", phone: "", propertyAddress: "", loanType: "Conventional", loanAmount: "" });
      setIsNewCaseOpen(false);
      setUploadMessage("");
      setNewComment("");
      if (isTestEnv) {
        setSelectedCase(newCase);
      } else {
        const refreshedCases = await loadDashboard();
        const syncedCase =
          refreshedCases.find((c) => c.id === newCase.id || c.caseNumber === newCase.caseNumber) || newCase;
        setSelectedCase(syncedCase);
      }
    } catch (error) {
      console.error(error);
      setNewCaseError("Failed to create case. Please retry.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCaseSelect = async (caseItem) => {
    setUploadMessage("");
    setNewComment("");
    setIsUploading(false);
    setIsCommenting(false);
    setIsStatusUpdating(false);
    setIsAiConfirmOpen(false);
    setAiMessage("");
    setIsCaseLoading(true);
    setCaseError("");

    try {
      // Fetch complete case data with fresh signed URLs from server
      const response = await fetchCaseById(caseItem.id);

      // Handle double-nested data structure: response.data.data
      // Backend returns: { status: 200, data: { id, documents, ... } }
      // fetchCaseById wraps it: { status: 200, data: { status: 200, data: {...} } }
      const freshCaseData = response?.data?.data || response?.data || {};

      // Use the normalizeCaseData helper to ensure proper status handling
      const normalizedCase = normalizeCaseData(freshCaseData, caseItem);

      setSelectedCase(normalizedCase);

      // Update caseFiles with fresh document data including signed URLs
      setCaseFiles((prev) => ({
        ...prev,
        [normalizedCase.id]: normalizedCase.documents
      }));

      // Initialize related state for the selected case
      setAiReports((prev) => ({
        ...prev,
        [normalizedCase.id]: Array.isArray(normalizedCase.aiReports)
          ? normalizedCase.aiReports
          : (prev[normalizedCase.id] || [])
      }));

      setCaseComments((prev) => ({
        ...prev,
        [normalizedCase.id]: Array.isArray(normalizedCase.comments) ? normalizedCase.comments : (prev[normalizedCase.id] || [])
      }));
    } catch (error) {
      console.error("Error loading case details:", error);
      setCaseError("Unable to load case details. Please try again.");
      // Use case data from list, but preserve existing documents from state
      // (don't overwrite with empty documents from the list)
      const existingDocs = caseFiles[caseItem.id] || [];
      setSelectedCase({
        ...caseItem,
        documents: existingDocs
      });
    } finally {
      setIsCaseLoading(false);
    }
  };

  // Handler for AMA chat case ID clicks - finds case by ID string and opens it
  const handleAMACaseClick = useCallback((caseId) => {

    // Normalize the clicked ID (remove "case-" prefix if present for comparison)
    const normalizedClickedId = String(caseId).toLowerCase().replace(/^case-/, '');

    // Find the case in the cases array by matching ID
    // Check all possible ID fields: id, caseId, case_id
    const foundCase = cases.find(c => {
      // Get all possible ID values from the case
      const possibleIds = [c.id, c.caseId, c.case_id].filter(Boolean);

      return possibleIds.some(possibleId => {
        const idStr = String(possibleId).toLowerCase();
        const normalizedId = idStr.replace(/^case-/, '');

        return (
          idStr === String(caseId).toLowerCase() ||  // Exact match
          normalizedId === normalizedClickedId ||     // Match without prefix
          idStr.includes(normalizedClickedId) ||      // Partial match (clicked ID in case ID)
          normalizedClickedId.includes(normalizedId.substring(0, 8))  // First 8 chars match
        );
      });
    });

    if (foundCase) {
      const foundId = foundCase.id || foundCase.caseId || foundCase.case_id;
      handleCaseSelect(foundCase);
    } else {
      console.warn(`[AMA] Case not found: ${caseId}.`);
      // Log all available IDs for debugging
      const availableIds = cases?.map(c => ({
        id: c.id,
        caseId: c.caseId,
        case_id: c.case_id
      })).slice(0, 5) || [];
      console.warn('[AMA] Sample available IDs (first 5):', availableIds);
    }
  }, [cases, handleCaseSelect]);

  // Handler for AMA chat task ID clicks - fetches task and opens associated case
  const handleAMATaskClick = async (taskId) => {
    try {
      // Fetch task details to get associated case_id
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/tasks/${encodeURIComponent(taskId)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const taskData = await response.json();
        const associatedCaseId = taskData?.data?.case_id || taskData?.case_id;

        if (associatedCaseId) {
          handleAMACaseClick(associatedCaseId);
        }
      } else {
        console.warn(`Task not found: ${taskId}`);
      }
    } catch (error) {
      console.error('Error fetching task:', error);
    }
  };

  // DEBUG: Expose test function on window for manual testing
  // Usage in browser console: window.__testCaseClick('case-xxxxx')
  useEffect(() => {
    window.__testCaseClick = (caseId) => {
      handleAMACaseClick(caseId);
    };
    window.__listCases = () => {
    };
    return () => {
      delete window.__testCaseClick;
      delete window.__listCases;
    };
  }, [cases, handleAMACaseClick]);

  const performUpload = async (files) => {
    if (!selectedCase || !files?.length) return;
    const maxSize = 25 * 1024 * 1024;
    const tooLarge = files.find((file) => file.size > maxSize);
    if (tooLarge) {
      setCaseError("File exceeds the 25 MB limit.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(5);
    setCaseError("");

    const totalBytes = files.reduce((sum, file) => sum + file.size, 0) || 1;
    let loadedBytes = 0;
    let uploadInterval = null;

    const toPayload = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        let lastLoaded = 0;

        reader.onprogress = (e) => {
          if (e.lengthComputable) {
            loadedBytes += e.loaded - lastLoaded;
            lastLoaded = e.loaded;
            const pct = Math.min(80, Math.round((loadedBytes / totalBytes) * 80));
            setUploadProgress((prev) => Math.max(prev, pct));
          }
        };

        reader.onload = () => {
          loadedBytes += file.size - lastLoaded;
          const pct = Math.min(80, Math.round((loadedBytes / totalBytes) * 80));
          setUploadProgress((prev) => Math.max(prev, pct));
          const result = reader.result;
          const base64 = typeof result === "string" ? result.split(",").pop() || "" : "";
          resolve({
            name: file.name,
            size: file.size,
            type: file.type || "application/octet-stream",
            content: base64
          });
        };
        reader.onerror = () => reject(new Error("Unable to read file"));
        reader.readAsDataURL(file);
      });

    try {
      const filePayloads = [];
      for (const file of files) {
        const payload = await toPayload(file);
        filePayloads.push(payload);
      }
      setUploadProgress((prev) => Math.max(prev, 85));
      uploadInterval = setInterval(
        () => setUploadProgress((prev) => (prev < 95 ? Math.min(95, prev + 2) : prev)),
        150
      );
      const response = await uploadCaseDocuments({ caseId: selectedCase.id, files: filePayloads });
      setUploadProgress(100);
      const serverDocs = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.documents)
          ? response.data.documents
          : Array.isArray(response.data?.files)
            ? response.data.files
            : [];
      const fallbackDocs =
        serverDocs.length > 0
          ? []
          : (files || []).map((file) => ({
            name: file.name,
            size: file.size,
            type: file.type,
            source: "Uploaded",
            uploadedAt: new Date().toISOString(),
            previewUrl: URL.createObjectURL(file),
            isObjectUrl: true
          }));
      const uploadedDocs = serverDocs.map((doc) => {
        return {
          name: doc.name,
          size: doc.size,
          type: doc.type || doc.mimeType || "application/pdf",
          source: doc.source || "Uploaded",
          uploadedAt: doc.uploadedAt || new Date().toISOString(),
          previewUrl: doc.signedUrl || doc.previewUrl || doc.downloadUrl || null,
          downloadUrl: doc.signedUrl || doc.downloadUrl || doc.previewUrl || null,
          signedUrl: doc.signedUrl || null,
          objectKey: doc.objectKey || null
        };
      });
      const updatedDocuments = [...uploadedDocs, ...fallbackDocs, ...(selectedCase.documents || [])];

      setCaseFiles((prev) => {
        return {
          ...prev,
          [selectedCase.id]: updatedDocuments
        };
      });

      // Update selectedCase with new documents
      setSelectedCase((prev) => ({
        ...prev,
        documents: updatedDocuments
      }));

      // Wait for server to process files and generate objectKey
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Refresh entire dashboard to get fresh data for all cases (same as status change)
      await loadDashboard();

      // Re-select the current case to get fresh documents with objectKey and signed URLs
      if (selectedCase?.id) {
        const refreshedCases = await loadDashboard();
        const currentCase = refreshedCases.find(c => c.id === selectedCase.id);
        if (currentCase) {
          await handleCaseSelect(currentCase);
        }
      }

      setUploadMessage(`${files.length} document${files.length > 1 ? "s" : ""} uploaded successfully.`);
    } catch (error) {
      console.error(error);
      setCaseError("Unable to upload documents. Please retry.");
    } finally {
      if (uploadInterval) clearInterval(uploadInterval);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDocumentUpload = (event) => {
    if (!selectedCase) return;
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;

    // SECURITY: Validate file types, sizes, and sanitize filenames
    const validation = validateFiles(files);

    if (!validation.valid) {
      setCaseError("Upload failed. Please check your files and try again.");
      return;
    }

    // Sanitize filenames to prevent path traversal attacks
    const sanitizedFiles = validation.validFiles.map(sanitizeFile);

    const existingNames = new Set((caseFiles[selectedCase.id] || []).map((f) => (f.name || "").toLowerCase()));
    const duplicates = sanitizedFiles.filter((file) => existingNames.has((file.name || "").toLowerCase()));
    if (duplicates.length > 0) {
      setPendingUploadFiles(sanitizedFiles);
      setDuplicateFiles(duplicates.map((f) => f.name));
      setIsDuplicatePromptOpen(true);
      return;
    }
    performUpload(sanitizedFiles);
  };

  const confirmDuplicateUpload = () => {
    const files = pendingUploadFiles;
    setIsDuplicatePromptOpen(false);
    setPendingUploadFiles([]);
    setDuplicateFiles([]);
    performUpload(files);
  };

  const cancelDuplicateUpload = () => {
    setIsDuplicatePromptOpen(false);
    setPendingUploadFiles([]);
    setDuplicateFiles([]);
  };

  const persistCheckoutSessionId = (sessionId) => {
    if (!sessionId) return;
    setCheckoutSessionId(sessionId);
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem("cm_last_checkout_session", sessionId);
      }
    } catch (error) {
      console.error("Unable to persist checkout session id", error);
    }
  };

  const buildReturnUrl = (flag) => {
    if (typeof window === "undefined") return undefined;
    const url = new URL(window.location.href);
    url.searchParams.set(flag, "true");
    if (flag === "success") {
      url.searchParams.delete("canceled");
    } else if (flag === "canceled") {
      url.searchParams.delete("success");
    }
    return url.toString();
  };

  const backupTokensForCheckout = () => {
    if (typeof window === "undefined") return;
    try {
      const tokens = getStoredTokens();
      if (!tokens) return;
      window.localStorage.setItem("cm_auth_tokens_backup", JSON.stringify(tokens));
    } catch (error) {
      console.warn("Unable to back up auth tokens before checkout", error);
    }
  };

  const handleStartCheckout = async (tierId = 'starter') => {
    if (typeof window === "undefined") return;
    setPaymentError("");
    setCheckoutMessage("");
    setCheckoutSuccess(false);
    setPaymentStep("Redirecting to Stripe...");
    setIsPaymentProcessing(true);
    try {
      setCheckoutType("subscription");
      try {
        window.localStorage.setItem("cm_checkout_type", "subscription");
        // Store the tier being purchased for success modal display
        window.localStorage.setItem("cm_checkout_tier", tierId);
      } catch { }
      backupTokensForCheckout();

      // Determine lookup key based on tier
      // Note: Backend only supports stripeLookupKey, not stripePriceId
      let lookupKey;
      if (tierId === 'pro') {
        lookupKey = PRO_LOOKUP_KEY;
      } else {
        lookupKey = STARTER_LOOKUP_KEY;
      }

      const payload = {
        lookupKey
      };

      const successUrl = buildReturnUrl("success");
      const cancelUrl = buildReturnUrl("canceled");
      const response = await createCheckoutSession({
        ...payload,
        successUrl,
        cancelUrl
      });
      const redirectUrl = response?.data?.url || response?.url;
      if (!redirectUrl) {
        throw new Error("Missing checkout url");
      }
      window.location.href = redirectUrl;
    } catch (error) {
      console.error("Checkout error", error);
      setPaymentError("Unable to start checkout. Please try again.");
    } finally {
      setIsPaymentProcessing(false);
      setPaymentStep("");
    }
  };

  const handleTopUpCheckout = async (lookupKey, options = {}) => {
    if (typeof window === "undefined") return;
    try {
      // Set post-checkout action based on source
      // Only open AI Report modal if coming from AI Report flow, not from other sources
      let postAction = 'none';
      if (options.source === 'developer-portal') {
        postAction = 'developer-portal';
      } else if (options.source === 'meeting-assistant') {
        postAction = 'meeting-assistant';
      } else if (!options.source || options.source === 'ai-report') {
        postAction = 'ai-report';
      }
      window.localStorage.setItem("cm_post_checkout_action", postAction);
      window.localStorage.setItem("cm_checkout_type", "topup");

      // Store current view to restore after checkout
      if (options.source === 'developer-portal') {
        window.localStorage.setItem("cm_post_checkout_view", "developer");
      } else if (options.source === 'meeting-assistant') {
        window.localStorage.setItem("cm_post_checkout_view", "assistant");
      }

      if (options.expectedCredits) {
        window.localStorage.setItem("cm_topup_expected", String(options.expectedCredits));
      }
      if (userMeta?.credit != null) {
        const creditValue = Number(String(userMeta.credit).replace(/[^0-9.]/g, ""));
        if (!Number.isNaN(creditValue)) {
          window.localStorage.setItem("cm_topup_previous", String(creditValue));
        }
      }
    } catch { }
    setCheckoutType("topup");
    setPaymentError("");
    setCheckoutMessage("");
    setCheckoutSuccess(false);
    setPaymentStep("Redirecting to Stripe...");
    setIsPaymentProcessing(true);
    try {
      backupTokensForCheckout();
      const successUrl = buildReturnUrl("success");
      const cancelUrl = buildReturnUrl("canceled");
      const payload = {
        lookupKey,
        successUrl,
        cancelUrl
      };
      const response = await createCheckoutSession(payload);
      const redirectUrl = response?.data?.url || response?.url;
      if (!redirectUrl) {
        throw new Error("Missing checkout url");
      }
      if (options.openInNewTab) {
        window.open(redirectUrl, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error("Top up checkout error", error);
      setPaymentError("Unable to start top up checkout. Please try again.");
    } finally {
      setIsPaymentProcessing(false);
      setPaymentStep("");
    }
  };

  const handleOpenPortal = async () => {
    setPaymentError("");
    setCheckoutMessage("");
    setIsPaymentProcessing(true);
    setPaymentStep("Opening billing portal...");
    try {
      const payload = checkoutSessionId ? { sessionId: checkoutSessionId } : {};
      const response = await createPortalSession(payload);
      const portalUrl = response?.data?.url || response?.url;
      if (!portalUrl) {
        throw new Error("Missing portal url");
      }
      window.location.href = portalUrl;
    } catch (error) {
      console.error("Portal error", error);
      setPaymentError(
        checkoutSessionId
          ? "Unable to open billing portal. Please try again."
          : "Unable to open billing portal. Please complete a checkout first."
      );
    } finally {
      setIsPaymentProcessing(false);
      setPaymentStep("");
    }
  };

  const clearStripeQueryParams = () => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.delete("success");
    url.searchParams.delete("canceled");
    url.searchParams.delete("session_id");
    url.searchParams.delete("sessionId");
    window.history.replaceState({}, document.title, url.toString());
  };

  const handleCheckoutSuccessAcknowledge = async () => {
    const email = userEmail || userMeta.email || "";
    if (!email) {
      setIsPaymentOpen(false);
      return;
    }
    setPaymentError("");
    setIsPaymentProcessing(true);
    setPaymentStep("Updating membership...");
    try {
      // Fetch latest subscription from backend using correct endpoint
      const subscriptionResponse = await fetchUserSubscription().catch((error) => {
        console.error("Subscription refresh error", error);
        return null;
      });

      if (subscriptionResponse?.data) {
        const subscription = subscriptionResponse.data;

        // Extract tier and credits from subscription response
        // Handle both flat and nested API response structures
        const tierFromSubscription = subscription?.tier || subscription?.data?.tier || subscription?.plan?.tier || subscription?.plan?.name;
        const resolvedMembershipNormalized = normalizeTier(tierFromSubscription);
        const resolvedMembership = resolvedMembershipNormalized.charAt(0).toUpperCase() + resolvedMembershipNormalized.slice(1);
        const resolvedCredit = subscription?.aiCreditsRemaining ?? subscription?.data?.aiCreditsRemaining ?? 0;

        // Preserve branding fields when updating subscription data
        setUserMetaSafe(prev => {
          const nextMeta = {
            // Keep existing branding fields
            agentName: prev?.agentName || "",
            agentTitle: prev?.agentTitle || "Real Estate Agent",
            agentPhone: prev?.agentPhone || "",
            agentEmail: prev?.agentEmail || "",
            agentPhoto: prev?.agentPhoto || null,
            brokerageName: prev?.brokerageName || "",
            brokerageLogo: prev?.brokerageLogo || null,
            accentColor: prev?.accentColor || "#d6b27f",
            defaultTemplate: prev?.defaultTemplate || "luxury-dark",
            // Update subscription fields
            membership: resolvedMembership,
            credit: resolvedCredit,
            email: subscription?.userId || subscription?.data?.userId || email,
            firstName: subscription?.firstName || subscription?.data?.firstName || "",
            lastName: subscription?.lastName || subscription?.data?.lastName || "",
            company: subscription?.company || subscription?.data?.company || ""
          };
          try {
            if (typeof window !== "undefined") {
              window.localStorage.setItem("cm_user_meta", JSON.stringify(nextMeta));
            }
          } catch { }
          return nextMeta;
        });
      }

      await loadDashboard();
      clearStripeQueryParams();
      setIsPaymentOpen(false);
      setCheckoutSuccess(false);
      setCheckoutMessage("");
    } catch (error) {
      console.error("Membership update error", error);
      setPaymentError("Subscription succeeded, but we could not update your membership. Please try again.");
    } finally {
      setIsPaymentProcessing(false);
      setPaymentStep("");
    }
  };

  const handleLoanAmountChange = (event) => {
    const raw = event.target.value || "";
    const numeric = raw.replace(/[^0-9]/g, "");
    const formatted = numeric ? Number(numeric).toLocaleString() : "";
    setCaseForm((prev) => ({ ...prev, loanAmount: formatted }));
  };

  const handleStatusChange = (event) => {
    const newStatus = event.target.value;
    if (!selectedCase) return;
    const caseIdValue = String(selectedCase.id);
    setIsStatusUpdating(true);
    updateCaseStatus({ caseId: selectedCase.id, newStatus })
      .then((response) => {
        const updatedStatus = typeof response.data?.status === "string" ? response.data.status : newStatus;
        setCases((prev) => {
          const nextCases = prev.map((c) => (String(c.id) === caseIdValue ? { ...c, status: updatedStatus } : c));
          setStats((prevStats) => ({ ...prevStats, ...computeCaseStats(nextCases) }));
          return nextCases;
        });
        setSelectedCase((prev) => (prev ? { ...prev, status: updatedStatus } : prev));

        // Trigger celebration modal for milestone statuses
        if (updatedStatus === "Approved" || updatedStatus === "Cleared to Close") {
          setCelebrationStatus(updatedStatus);
          // Celebration effects will trigger when user clicks Continue button
        }

        loadDashboard();
      })
      .catch((error) => {
        console.error(error);
        setCaseError("Unable to update case status. Please retry.");
      })
      .finally(() => setTimeout(() => setIsStatusUpdating(false), 400));
  };

  const handleCaseUpdate = (updatedCaseData) => {
    const caseIdValue = String(updatedCaseData.id || updatedCaseData.caseId || updatedCaseData.case_id);
    setCases((prev) => {
      const nextCases = prev.map((c) =>
        String(c.id || c.caseId || c.case_id) === caseIdValue ? { ...c, ...updatedCaseData } : c
      );
      setStats((prevStats) => ({ ...prevStats, ...computeCaseStats(nextCases) }));
      return nextCases;
    });
    setSelectedCase((prev) => (prev ? { ...prev, ...updatedCaseData } : prev));
  };

  const handleAddComment = async (event, text = null, replyTo = null) => {
    if (event && event.preventDefault) event.preventDefault();
    const textToUse = text !== null ? text : newComment;
    if (!selectedCase || !textToUse.trim()) return;

    setIsCommenting(true);
    setCaseError(""); // Clear any previous errors

    const commentText = textToUse.trim();
    const caseId = selectedCase.id || selectedCase.caseId || selectedCase.case_id;

    try {
      // Build comment payload with optional reply context
      const commentPayload = {
        caseId: caseId,
        text: commentText,
        author: userEmail || "Loan Admin"
      };

      // Include reply context if replying to another comment
      if (replyTo) {
        commentPayload.replyToId = replyTo.id;
        commentPayload.replyToText = replyTo.text;
        commentPayload.replyToAuthor = replyTo.author;
      }

      // Call the backend API to persist the comment
      const response = await addCaseComment(commentPayload);


      // Extract the comment from the response - handle various nesting levels
      let savedComment = null;

      // Try different response structures
      if (response?.data?.data?.comment) {
        savedComment = response.data.data.comment;
      } else if (response?.data?.comment) {
        savedComment = response.data.comment;
      } else if (response?.data?.data) {
        savedComment = response.data.data;
      } else if (response?.data) {
        savedComment = response.data;
      }


      // Ensure we have all required fields
      const normalizedComment = {
        id: savedComment?.id || savedComment?._id || `temp-${Date.now()}`,
        text: savedComment?.text || commentText,
        author: savedComment?.author || savedComment?.user || userEmail || "Loan Admin",
        createdAt: savedComment?.createdAt || savedComment?.created_at || savedComment?.timestamp || new Date().toISOString(),
        // Include reply context if this is a threaded reply
        ...(replyTo && {
          replyToId: replyTo.id,
          replyToText: replyTo.text,
          replyToAuthor: replyTo.author
        })
      };


      // Update local state with the saved comment
      setCaseComments((prev) => {
        const existing = prev[selectedCase.id] || [];
        return { ...prev, [selectedCase.id]: [normalizedComment, ...existing] };
      });

      setNewComment("");
    } catch (error) {
      console.error("Failed to add comment:", error);
      setCaseError(error?.message || "Unable to add comment. Please try again.");
    } finally {
      setTimeout(() => {
        setIsCommenting(false);
      }, 500);
    }
  };

  const resolveFileLink = async (file) =>
    resolveFileLinkHelper(file, selectedCase, fetchSignedUrl, fetchCaseById);

  const handlePreview = async (file) => {
    setIsPreviewLoading(true);
    setCaseError(""); // Clear any previous errors

    try {
      if (!file) return;


      // Normalize chat attachment keys to standard objectKey
      let objectKey = file.objectKey || file.s3_key || file.object_key;

      // If we still don't have an objectKey but have a selected case, try to find the file in the case documents
      if (!objectKey && selectedCase) {
        const currentFiles = caseFiles[selectedCase.id] || selectedCase.documents || [];
        const latestFile = currentFiles.find(f =>
          f.name === file.name ||
          f.fileName === file.name ||
          f.originalName === file.name
        );
        if (latestFile && latestFile.objectKey) {
          objectKey = latestFile.objectKey;
          // Merge properties from the found file to ensure we better metadata
          file = { ...latestFile, ...file };
        }
      }

      let link = null;

      // 1. Try to get a fresh signed URL from API if we have an objectKey
      // This logic mirrors LenderChat.js exactly
      if (objectKey) {
        try {
          // Use the imported fetchSignedUrl directly
          const response = await fetchSignedUrl(objectKey);
          // Check all possible return paths as per LenderChat.js
          link = response?.data?.url || response?.data?.signedUrl || response?.url || response?.signedUrl;
        } catch (error) {
          console.error("❌ Failed to fetch signed URL:", error);
        }
      }

      // 2. Fallback to existing properties if API fetch failed or no objectKey
      if (!link) {
        link = file.signed_url || file.url || file.signedUrl || file.previewUrl || file.downloadUrl;
      }

      // 3. Last resort fallback (using the helper if available, but logic above should cover most cases)
      if (!link && objectKey) {
        try {
          link = await resolveFileLink(file);
        } catch (e) {
          console.warn("Helper resolution also failed");
        }
      }

      if (!link) {
        setCaseError("Preview not available for this file.");
        return;
      }

      // Resolve name aggressively
      const docName = file.name || file.filename || file.fileName || file.originalName || file.original_filename || "Untitled Document";

      setPreviewDoc({
        name: docName,
        url: link,
        type: file.type || file.file_type || file.contentType || "application/pdf",
        isObjectUrl: !!file.isObjectUrl,
        canSave: Boolean(file.canSave),
        saveName: file.saveName || docName
      });
    } catch (error) {
      console.error("Error loading document preview:", error);
      setCaseError("Unable to load document preview. Please try again.");
    } finally {
      setTimeout(() => setIsPreviewLoading(false), 150);
    }
  };

  const handleSavePreview = async ({ name, url, type }) => {
    if (!selectedCase || !name || !url) return false;
    setIsPreviewSaving(true);
    setCaseError("");
    try {
      const normalizedName = name.trim();
      const needsPdfExt = type === "application/pdf" && !normalizedName.toLowerCase().endsWith(".pdf");
      const fileName = needsPdfExt ? `${normalizedName}.pdf` : normalizedName;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Unable to fetch file (${response.status})`);
      }
      const buffer = await response.arrayBuffer();
      if (!buffer || buffer.byteLength === 0) {
        throw new Error("Downloaded file is empty.");
      }
      if ((type || "").includes("pdf")) {
        const header = new TextDecoder("ascii").decode(buffer.slice(0, 4));
        if (header !== "%PDF") {
          throw new Error("File is not a valid PDF.");
        }
      }
      const mimeType = type || "application/pdf";
      const file = new File([buffer], fileName, { type: mimeType });
      const validation = validateFiles([file]);
      if (!validation.valid) {
        setCaseError("Upload failed. Please check your file and try again.");
        return false;
      }

      const sanitizedFiles = validation.validFiles.map(sanitizeFile);
      const existingNames = new Set((caseFiles[selectedCase.id] || []).map((f) => (f.name || "").toLowerCase()));
      const duplicates = sanitizedFiles.filter((incoming) => existingNames.has((incoming.name || "").toLowerCase()));
      if (duplicates.length > 0) {
        setPendingUploadFiles(sanitizedFiles);
        setDuplicateFiles(duplicates.map((incoming) => incoming.name));
        setIsDuplicatePromptOpen(true);
        return false;
      }

      const arrayBufferToBase64 = (arrBuffer) => {
        let binary = "";
        const bytes = new Uint8Array(arrBuffer);
        const chunkSize = 0x8000;
        for (let i = 0; i < bytes.length; i += chunkSize) {
          binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
        }
        return btoa(binary);
      };

      setIsUploading(true);
      setUploadProgress(10);
      const base64 = arrayBufferToBase64(buffer);
      const filePayloads = [
        {
          name: file.name,
          size: file.size,
          type: mimeType,
          content: base64
        }
      ];

      setUploadProgress(60);
      const responseUpload = await uploadCaseDocuments({ caseId: selectedCase.id, files: filePayloads });
      setUploadProgress(100);
      const serverDocs = Array.isArray(responseUpload.data)
        ? responseUpload.data
        : Array.isArray(responseUpload.data?.documents)
          ? responseUpload.data.documents
          : Array.isArray(responseUpload.data?.files)
            ? responseUpload.data.files
            : [];
      const fallbackDocs =
        serverDocs.length > 0
          ? []
          : [
            {
              name: file.name,
              size: file.size,
              type: mimeType,
              source: "Uploaded",
              uploadedAt: new Date().toISOString(),
              previewUrl: URL.createObjectURL(new Blob([buffer], { type: mimeType })),
              isObjectUrl: true
            }
          ];
      const uploadedDocs = serverDocs.map((doc) => ({
        name: doc.name,
        size: doc.size,
        type: doc.type || doc.mimeType || "application/pdf",
        source: doc.source || "Uploaded",
        uploadedAt: doc.uploadedAt || new Date().toISOString(),
        previewUrl: doc.signedUrl || doc.previewUrl || doc.downloadUrl || null,
        downloadUrl: doc.signedUrl || doc.downloadUrl || doc.previewUrl || null,
        signedUrl: doc.signedUrl || null,
        objectKey: doc.objectKey || null
      }));
      const updatedDocuments = [...uploadedDocs, ...fallbackDocs, ...(selectedCase.documents || [])];
      setCaseFiles((prev) => ({
        ...prev,
        [selectedCase.id]: updatedDocuments
      }));
      setSelectedCase((prev) => ({
        ...prev,
        documents: updatedDocuments
      }));
      setUploadMessage("Document uploaded successfully.");

      await new Promise((resolve) => setTimeout(resolve, 1500));
      await loadDashboard();
      if (selectedCase?.id) {
        const refreshedCases = await loadDashboard();
        const currentCase = refreshedCases.find(c => c.id === selectedCase.id);
        if (currentCase) {
          await handleCaseSelect(currentCase);
        }
      }
      return true;
    } catch (error) {
      console.error("Preview save failed", error);
      setCaseError("Unable to save document. Please try again.");
      return false;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setIsPreviewSaving(false);
    }
  };

  const handleDeleteFile = (file) => {
    if (!selectedCase || !file) return;
    setDeleteCandidate(file);
    setFileDeleteTarget(file.name);
  };

  const confirmDeleteFile = async () => {
    if (!selectedCase || !deleteCandidate) return;
    const objectKey = deleteCandidate.objectKey;
    if (!objectKey) {
      alert("Cannot delete: Missing object key");
      setDeleteCandidate(null);
      setFileDeleteTarget(null);
      return;
    }

    setIsDeletingFile(true);
    try {
      // Call the real delete API
      await deleteDocument(objectKey);

      // Remove from local state
      setCaseFiles((prev) => {
        const existing = prev[selectedCase.id] || [];
        const filtered = existing.filter((f) => f.objectKey !== objectKey);
        return { ...prev, [selectedCase.id]: filtered };
      });

      // Refresh case data to ensure consistency
      await loadDashboard();
      if (selectedCase?.id) {
        const refreshedCases = await loadDashboard();
        const currentCase = refreshedCases.find(c => c.id === selectedCase.id);
        if (currentCase) {
          await handleCaseSelect(currentCase);
        }
      }
    } catch (error) {
      console.error("Delete document failed:", error);
      alert("Failed to delete document. Please try again.");
    } finally {
      setTimeout(() => {
        setIsDeletingFile(false);
        setDeleteCandidate(null);
        setFileDeleteTarget(null);
      }, 300);
    }
  };

  const cancelDelete = () => {
    setDeleteCandidate(null);
    setFileDeleteTarget(null);
  };

  const closePreview = () => {
    if (previewDoc?.isObjectUrl) {
      URL.revokeObjectURL(previewDoc.url);
    }
    setPreviewDoc(null);
    setIsPreviewLoading(false);
  };

  const handleAiGenerateClick = () => {
    setIsAiConfirmOpen(true);
  };

  const formatRunLabel = (runId, startedAt) => {
    if (runId) {
      const match = String(runId).match(/^run-(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})$/);
      if (match) {
        const [, year, month, day, hour, minute, second] = match;
        return `Run ${year}-${month}-${day} ${hour}:${minute}:${second}`;
      }
    }
    if (startedAt) {
      const date = new Date(startedAt);
      if (!isNaN(date.getTime())) {
        const pad = (value) => String(value).padStart(2, "0");
        const label = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
        return `Run ${label}`;
      }
    }
    return "Run";
  };

  const handleAiConfirm = (config) => {
    const selectedCaseKey = selectedCase?.id || selectedCase?.caseId || selectedCase?.case_id;
    if (!selectedCaseKey) return;

    // Prevent duplicate requests if already generating
    if (isAiGenerating) {
      console.warn("AI report generation already in progress, ignoring duplicate request");
      return;
    }

    const reportTier = config.reportType || "basic";
    const reportCost = reportTier === "advanced" ? 1000 : 150;
    const acceleration = config.acceleration;
    const accelerationCost = acceleration?.credits || 0;
    const totalCost = config.totalCredits || (reportCost + accelerationCost);

    // Calculate delivery hours based on acceleration or standard tier
    const deliveryHours = acceleration?.hours || (userMeta?.membership ?
      ({ free: 9, starter: 6, pro: 3, ultra: 1 }[userMeta.membership.toLowerCase()] || 9) : 9);

    setIsAiGenerating(true);
    setIsAiConfirmOpen(false);
    setCaseError(""); // Clear any previous errors
    setAiMessage(""); // Clear any previous messages

    const requestPayload = {
      caseId: selectedCaseKey,
      membership: userMeta?.membership,
      reportType: config.reportType,
      delivery: config.acceleration?.level || "standard",
      totalCost: totalCost
    };

    requestAiReport(requestPayload)
      .then((response) => {

        // Handle nested response structure: response.data.data or response.data
        const responseData = response.data?.data || response.data;

        const responseStatus = response?.status;
        // Check if the request was successful with new API format
        if (responseData?.requestId) {
          const requestId = responseData.requestId;
          const status = responseData.status; // "RUNNING", "SUCCEEDED", "FAILED", etc.
          const startedAt = responseData.startedAt;
          const trackingUrl = responseData.trackingUrl;

          // Calculate ETA based on selected delivery time
          const etaDate = new Date();
          etaDate.setHours(etaDate.getHours() + deliveryHours);
          const etaDisplay = etaDate.toLocaleString();

          const resolvedStatus = status || "PENDING";
          const accelerationMsg = acceleration ? ` (${acceleration.label} - ${deliveryHours}h delivery)` : ` (${deliveryHours}h delivery)`;
          setAiMessage(
            `AI report generation (${reportTier === "advanced" ? "Advanced Report" : "Basic"})${accelerationMsg} started successfully! Request ID: ${requestId}. ` +
            `Status: ${resolvedStatus}. Expected completion: ${etaDisplay}`
          );
          setPipelineStatus({
            status: resolvedStatus,
            run_id: requestId,
            started_at: startedAt || new Date().toISOString()
          });
          if (responseStatus === 202 && trackingUrl) {
            startRunPolling(trackingUrl);
          }

          // Backend already deducts credits, just refresh to get updated balance
          // DO NOT call deductSubscriptionCredits here - it causes double deduction!
          const creditsBefore = userMeta?.credit || 0;
          refreshSubscriptionMeta()
            .then(() => {
            })
            .catch((refreshError) => {
              console.error("Failed to refresh subscription after AI report request:", refreshError);
            });

          // Add to AI reports section (NOT to caseFiles)
          setAiReports((prev) => ({
            ...prev,
            [selectedCaseKey]: [
              ...(prev[selectedCaseKey] || []),
              {
                id: requestId,
                status: status,
                eta: etaDisplay,
                name: formatRunLabel(requestId, startedAt),
                url: null,
                trackingUrl: trackingUrl,
                startedAt: startedAt || new Date().toISOString()
              }
            ]
          }));
        } else {
          // API didn't return expected format
          console.warn("API did not return expected format. Full response:", response);
          const errorDetail = responseData?.error || responseData?.message || "Unknown error";
          setCaseError(
            `AI report generation could not be started: ${errorDetail}. Please check your case documents and try again.`
          );
        }
      })
      .catch((error) => {
        console.error("AI report generation error:", error);

        if (error.status === 429 && error.data?.detail) {
          const detail = error.data.detail;

          if (detail.existing_status) {
            setCaseError(`A pipeline is already processing this case. Please wait for it to finish.`);
          } else if (detail.cooldown_remaining_seconds) {
            setCaseError(`Please wait ${detail.cooldown_remaining_seconds} seconds before starting another pipeline run.`);
          } else {
            setCaseError(detail.message || "Rate limit exceeded. Please try again later.");
          }
        } else {
          setCaseError(
            error.message || "Unable to generate AI report. Please ensure all documents are uploaded and try again."
          );
        }
      })
      .finally(() => {
        setIsAiGenerating(false);
        // Clear the success message after 10 seconds
        setTimeout(() => setAiMessage(""), 10000);
      });
  };
  return (
    <div className="h-screen dark:bg-slate-950 bg-gray-50 dark:text-slate-100 text-gray-900 font-sans flex relative">
      {showInitialLoader && (
        <div className="absolute inset-0 z-50 flex items-center justify-center dark:bg-slate-950/80 bg-slate-500/50 backdrop-blur">
          <div className="dark:bg-slate-900/80 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl px-6 py-4 shadow-lg flex items-center gap-3">
            <Loader2 className="w-5 h-5 dark:text-sky-400 text-sky-600 animate-spin" />
            <div>
              <p className="text-sm font-semibold dark:text-white text-slate-900">Loading your dashboard</p>
              <p className="text-xs text-slate-500">Fetching latest cases...</p>
            </div>
          </div>
        </div>
      )}
      {isRefreshing && !showInitialLoader && (
        <div className="fixed top-4 right-4 z-40 dark:bg-slate-900/90 bg-white border dark:border-slate-800 border-slate-200 rounded-full px-3 py-1.5 shadow flex items-center gap-2 text-xs dark:text-slate-200 text-slate-700">
          <Loader2 className="w-4 h-4 animate-spin dark:text-sky-400 text-sky-600" />
          <span>Refreshing</span>
        </div>
      )}

      {/* Processing / Loading notification for checkout */}
      {!checkoutSuccess && checkoutMessage && (checkoutMessage.includes("Verifying") || checkoutMessage.includes("Finalizing")) && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[200] bg-indigo-900/95 border border-indigo-500 rounded-xl px-6 py-4 shadow-2xl backdrop-blur-md flex items-center gap-4 text-sm animate-in fade-in slide-in-from-top-4 duration-500 min-w-[320px]">
          <Loader2 className="w-5 h-5 text-indigo-300 animate-spin" />
          <div>
            <p className="font-semibold text-white text-base">Processing Payment</p>
            <p className="text-xs text-indigo-200 mt-0.5">{checkoutMessage}</p>
          </div>
        </div>
      )}

      {/* Success notification after credit purchase */}
      {/* Success notification after credit purchase */}
      {checkoutSuccess && checkoutMessage && checkoutType === "topup" && !isPaymentProcessing && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[200] bg-emerald-900/95 border border-emerald-600 rounded-xl px-6 py-4 shadow-2xl backdrop-blur-md flex items-center gap-4 text-sm animate-in fade-in slide-in-from-top-4 duration-500 min-w-[320px] justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
            <div>
              <p className="font-semibold text-white text-base">Payment Successful!</p>
              <p className="text-xs text-emerald-200 mt-0.5">
                Thank you for your purchase. Balance: {userMeta?.credit?.toLocaleString() || 0} credits
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setCheckoutSuccess(false);
              setCheckoutMessage("");
            }}
            className="ml-2 text-emerald-300 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Error notification for credit purchase failure */}
      {!checkoutSuccess && checkoutMessage && !checkoutMessage.includes("Verifying") && !checkoutMessage.includes("Finalizing") && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[200] bg-rose-950/95 border border-rose-600 rounded-xl px-6 py-4 shadow-2xl backdrop-blur-md flex items-center gap-4 text-sm animate-in fade-in slide-in-from-top-4 duration-500 min-w-[320px] justify-between">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-2 h-2 rounded-full bg-rose-500 animate-pulse"></div>
            <div>
              <p className="font-semibold text-white text-base">
                {checkoutMessage.toLowerCase().includes("cancel") ? "Purchase Canceled" : "Update Failed"}
              </p>
              <p className="text-xs text-rose-200 mt-0.5">
                {checkoutMessage}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!checkoutMessage.toLowerCase().includes("cancel") && (
              <button
                onClick={async () => {
                  setCheckoutMessage("Refreshing subscription status...");
                  try {
                    const subscriptionResponse = await fetchUserSubscription();
                    const subscription = subscriptionResponse?.data;
                    if (subscription) {
                      const tierFromSubscription = subscription?.tier || subscription?.plan?.tier || subscription?.plan?.name;
                      const resolvedTier = normalizeTier(tierFromSubscription);
                      console.log("[Checkout] Manual refresh - tier:", resolvedTier, "Full response:", subscription);

                      if (resolvedTier && resolvedTier !== "free") {
                        // Subscription verified! Preserve branding fields
                        const nextMembership = resolvedTier.charAt(0).toUpperCase() + resolvedTier.slice(1);
                        const credits = subscription?.aiCreditsRemaining ?? 0;
                        setUserMetaSafe(prev => ({
                          // Keep existing branding fields
                          agentName: prev?.agentName || "",
                          agentTitle: prev?.agentTitle || "Real Estate Agent",
                          agentPhone: prev?.agentPhone || "",
                          agentEmail: prev?.agentEmail || "",
                          agentPhoto: prev?.agentPhoto || null,
                          brokerageName: prev?.brokerageName || "",
                          brokerageLogo: prev?.brokerageLogo || null,
                          accentColor: prev?.accentColor || "#d6b27f",
                          defaultTemplate: prev?.defaultTemplate || "luxury-dark",
                          // Update subscription fields
                          membership: nextMembership,
                          credit: credits,
                          email: subscription?.userId || userEmail || "",
                          avatar_url: subscription?.avatar_url || prev?.avatar_url || "",
                          firstName: subscription?.firstName || prev?.firstName || "",
                          lastName: subscription?.lastName || prev?.lastName || "",
                          company: subscription?.company || prev?.company || ""
                        }));
                        setSubscriptionData(subscription);
                        setCheckoutSuccess(true);
                        setCheckoutMessage("Subscription Updated");
                      } else {
                        // Still showing free tier
                        setCheckoutMessage(`Still showing tier: ${resolvedTier || 'unknown'}. Backend may still be processing. Try again in a few seconds.`);
                      }
                    }
                  } catch (err) {
                    console.error("[Checkout] Manual refresh failed:", err);
                    setCheckoutMessage(`Refresh failed: ${err.message}`);
                  }
                }}
                className="px-3 py-1.5 text-xs font-medium bg-rose-800 hover:bg-rose-700 text-white rounded-lg transition-colors"
              >
                Retry
              </button>
            )}
            <button
              onClick={() => {
                setCheckoutSuccess(true); // Reset to hide error
                setCheckoutMessage("");
              }}
              className="px-3 py-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
      {isCaseLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center dark:bg-slate-950/60 bg-slate-500/50 backdrop-blur-sm">
          <div className="dark:bg-slate-900/95 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl px-8 py-6 shadow-2xl flex items-center gap-4">
            <Loader2 className="w-6 h-6 dark:text-sky-400 text-sky-600 animate-spin" />
            <div>
              <p className="text-base font-semibold dark:text-white text-slate-900">Loading case details</p>
              <p className="text-sm text-slate-400">Fetching documents...</p>
            </div>
          </div>
        </div>
      )}
      {isAiGenerating && !isAiConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center dark:bg-slate-950/60 bg-slate-500/50 backdrop-blur-sm">
          <div className="dark:bg-slate-900/95 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl px-8 py-6 shadow-2xl flex items-center gap-4">
            <Loader2 className="w-6 h-6 dark:text-sky-400 text-sky-600 animate-spin" />
            <div>
              <p className="text-base font-semibold dark:text-white text-slate-900">Generating AI Report</p>
              <p className="text-sm text-slate-400">Starting Step Functions execution...</p>
            </div>
          </div>
        </div>
      )}


      <Sidebar
        openNewCaseModal={openNewCaseModal}
        onNavigate={setCurrentView}
        currentView={currentView}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        featureFlags={featureFlags}
        unreadChatCount={unreadChatCount}
        userMeta={userMeta}
        userEmail={userEmail}
        onLogout={onLogout}
        setIsAccountSettingsOpen={setIsAccountSettingsOpen}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
        onOpenDialer={phoneCall.openDialer}
      />

      {currentView === 'virtual-staging' && !isSidebarOpen && (
        <button
          type="button"
          onClick={() => setIsSidebarOpen(true)}
          aria-label="Open menu"
          className="md:hidden fixed top-2 right-3 z-[60] p-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 shadow-lg text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      <main className="flex-1 flex flex-col min-w-0">
        {currentView !== 'virtual-staging' && (
          <DashboardHeader
            userEmail={userEmail}
            userMeta={userMeta}
            isProfileOpen={isProfileOpen}
            setIsProfileOpen={setIsProfileOpen}
            profileMenuRef={profileMenuRef}
            onLogout={onLogout}
            setIsAccountSettingsOpen={setIsAccountSettingsOpen}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            handleSearchSubmit={handleSearchSubmit}
            openNewCaseModal={openNewCaseModal}
            onNewAgent={() => setIsAgentModalOpen(true)}
            onMenuClick={() => setIsSidebarOpen(true)}
          />
        )}

        <div className={`flex-1 min-h-0 ${currentView === "assistant" || currentView === "chat" || currentView === "marketing" || currentView === "organization" || currentView === "realtor-referral" || currentView === "insurance-referral" || currentView === "leads" || currentView === "partners" || currentView === "sms" || currentView === "fb-monitor" || currentView === "virtual-staging" || currentView === "ai-images" || currentView === "listings" ? "overflow-hidden flex flex-col" : "p-6 overflow-auto"}`}>
          {caseError && (
            <div className="mb-4 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-lg px-4 py-3">
              {caseError}
            </div>
          )}
          {currentView === 'cases' && (
            <>
              <DashboardStats stats={stats} cases={cases} setStatusFilter={setStatusFilter} statusFilter={statusFilter} />

              <div className="flex justify-between items-center mb-4">
                <h2 className="dark:text-white text-slate-900 font-bold">Recent Files</h2>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <select
                      value={sortConfig.key}
                      onChange={(e) => setSortConfig({ ...sortConfig, key: e.target.value })}
                      className="dark:bg-slate-800 bg-white dark:text-slate-300 text-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 border dark:border-slate-700 border-slate-200"
                    >
                      <option value="date">Sort by Date</option>
                      <option value="name">Sort by Name</option>
                      <option value="loanAmount">Sort by Loan Amount</option>
                    </select>
                    <button
                      onClick={() =>
                        setSortConfig({
                          ...sortConfig,
                          direction: sortConfig.direction === "ascending" ? "descending" : "ascending",
                        })
                      }
                      className="p-2 rounded-lg dark:bg-slate-800 bg-white dark:text-slate-400 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 border dark:border-slate-700 border-slate-200"
                    >
                      {sortConfig.direction === "ascending" ? "↑" : "↓"}
                    </button>
                  </div>
                  <button
                    onClick={() => setViewMode("table")}
                    className={`p-2 rounded-lg transition-colors border dark:border-transparent border-slate-200 ${viewMode === "table" ? "bg-sky-600 text-white border-transparent" : "dark:bg-slate-800 bg-white dark:text-slate-400 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                      }`}
                  >
                    <Rows className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode("kanban")}
                    className={`p-2 rounded-lg transition-colors border dark:border-transparent border-slate-200 ${viewMode === "kanban" ? "bg-sky-600 text-white border-transparent" : "dark:bg-slate-800 bg-white dark:text-slate-400 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                      }`}
                  >
                    <LayoutGrid className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          )}
          {currentView === "teams" && (
            <div className="flex items-center justify-between mb-4">
              {/* TeamsManager handles its own header */}
            </div>
          )}
          {currentView === "collab-cases" && (
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="dark:text-white text-slate-900 font-bold">Shared Cases</h2>
                <p className="text-sm text-slate-400">Cases shared with you by teammates.</p>
              </div>
            </div>
          )}
          {currentView === "chat" && (
            <div className="flex items-center justify-between mb-4" />
          )}
          {currentView === "assistant" && (
            <>
            {/* Hero Banner - Compact */}
            <div className="relative overflow-hidden -mx-6 -mt-6 px-6 py-4 mb-0">
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(120deg, rgba(99, 102, 241, 0.95), rgba(15, 23, 42, 0.95))'
                }}
              />
              <div className="relative max-w-5xl mx-auto">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-white">
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                      <Bot className="w-5 h-5" /> AI Agent Team
                    </h2>
                    <p className="text-xs text-white/70 mt-0.5">
                      Your AI-powered workforce to build business and serve customers
                    </p>
                  </div>
                  <div className="hidden sm:flex gap-2 text-xs">
                    <span className="px-3 py-1.5 rounded-full bg-white/15 text-white/80 font-medium">
                      Case Analysis
                    </span>
                    <span className="px-3 py-1.5 rounded-full bg-white/15 text-white/80 font-medium">
                      Content Creation
                    </span>
                    <span className="px-3 py-1.5 rounded-full bg-white/15 text-white/80 font-medium">
                      Client Support
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Agent Tabs */}
            <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setAssistantTab("case")}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${assistantTab === "case"
                    ? "bg-sky-600 text-white"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  Case Assistant
                </button>
                <button
                  onClick={() => setAssistantTab("meeting")}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${assistantTab === "meeting"
                    ? "bg-sky-600 text-white"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  Meeting Assistant
                </button>
                {(userMeta?.membership?.toLowerCase() === 'ultra') && (
                  <button
                    onClick={() => setAssistantTab("intake")}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${assistantTab === "intake"
                      ? "bg-sky-600 text-white"
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                  >
                    Intake Assistant
                  </button>
                )}
                {featureFlags.hasMarketingAssistant && (
                  <button
                    onClick={() => setAssistantTab("marketing")}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${assistantTab === "marketing"
                      ? "bg-sky-600 text-white"
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                  >
                    Marketing Assistant
                  </button>
                )}
                {featureFlags.hasSocialMedia && (
                  <button
                    onClick={() => setAssistantTab("design")}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${assistantTab === "design"
                      ? "bg-sky-600 text-white"
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                  >
                    Design Assistant
                  </button>
                )}
                {featureFlags.hasSocialMedia && (
                  <button
                    onClick={() => setAssistantTab("video")}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${assistantTab === "video"
                      ? "bg-sky-600 text-white"
                      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                  >
                    Video Assistant
                  </button>
                )}
              </div>
            </div>
            </>
          )}
          <AnimatePresence mode="wait">
            <motion.div
              key={viewMode}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className={currentView === "assistant" || currentView === "chat" || currentView === "training" || currentView === "support" || currentView === "tasks" || currentView === "affiliate" || currentView === "developer" || currentView === "admin-usage" || currentView === "notes" || currentView === "calendar" || currentView === "marketing" || currentView === "organization" || currentView === "leads" || currentView === "partners" || currentView === "realtor-referral" || currentView === "insurance-referral" || currentView === "sms" || currentView === "fb-monitor" || currentView === "virtual-staging"
                ? "flex-1 flex flex-col min-h-0"
                : ""}
            >
              {currentView === 'cases' ? (
                showInitialLoader ? (
                  <CasesSkeleton />
                ) : viewMode === 'table' ? (
                  <CasesTable
                    cases={cases}
                    filteredBorrowers={filteredBorrowers}
                    paginatedBorrowers={paginatedBorrowers}
                    sortConfig={sortConfig}
                    handleSort={handleSort}
                    handleCaseSelect={handleCaseSelect}
                    openNewCaseModal={openNewCaseModal}
                    searchTerm={searchTerm}
                  />
                ) : (
                  <KanbanBoard cases={filteredBorrowers} onStatusChange={handleKanbanStatusChange} onCaseSelect={handleCaseSelect} />
                )
              ) : currentView === "teams" ? (
                <TeamsManager
                  userEmail={userEmail}
                  subscription={subscriptionData}
                  onUpgradePlan={() => setIsAccountSettingsOpen(true)}
                  onCaseSelect={handleCaseSelect}
                  initialMembers={teamMembers}
                  isPreloaded={isTeamPreloaded}
                />
              ) : currentView === "collab-cases" ? (
                <CollabCasesList
                  cases={filteredCollabCases}
                  isLoading={isCollabCasesLoading}
                  error={collabCasesError}
                  onCaseSelect={handleCaseSelect}
                />
              ) : currentView === "chat" ? (
                <TeamChat
                  userEmail={userEmail}
                  initialConversations={chatConversations}
                  isPreloaded={isChatPreloaded}
                  onPreview={handlePreview}
                  featureFlags={featureFlags}
                  subscription={subscriptionData}
                  onUpgradePlan={handleOpenPortal}
                  onUnreadCountChange={setUnreadChatCount}
                />
              ) : currentView === "assistant" ? (
                assistantTab === "case" ? (
                  <LenderChat
                    cases={cases}
                    caseFiles={caseFiles}
                    onOpenNewCase={openNewCaseModal}
                    onUpdateCaseFiles={setCaseFiles}
                    isLoading={showInitialLoader}
                    userCredits={userMeta?.credit || 0}
                  />
                ) : assistantTab === "meeting" ? (
                  <MeetingAssistant
                    userCredits={userMeta?.credit || 0}
                    onRefreshCredits={refreshSubscriptionMeta}
                    creditPacks={creditPacks}
                    onTopUp={(value) => {
                      if (typeof value === "object" && value) {
                        const lookup = value.lookupKey;
                        const credits = Number(String(value.credits || "").replace(/[^0-9]/g, ""));
                        if (lookup && lookup.startsWith("credits_")) {
                          handleTopUpCheckout(lookup, {
                            openInNewTab: Boolean(value.openInNewTab),
                            expectedCredits: credits || undefined,
                            source: 'meeting-assistant'
                          });
                        }
                      }
                    }}
                    isPaymentProcessing={isPaymentProcessing}
                  />
                ) : assistantTab === "intake" && userMeta?.membership?.toLowerCase() === 'ultra' ? (
                  <IntakeAssistant
                    userEmail={userEmail}
                    userMeta={userMeta}
                    userTier={userMeta?.membership || 'free'}
                    onRefreshCredits={refreshSubscriptionMeta}
                  />
                ) : assistantTab === "marketing" && featureFlags.hasMarketingAssistant ? (
                  <MarketingAgent
                    userEmail={userEmail}
                    subscription={subscriptionData}
                    onUpgradePlan={handleOpenPortal}
                    userCredits={userMeta?.credit || 0}
                    onRefreshCredits={refreshSubscriptionMeta}
                  />
                ) : assistantTab === "design" && featureFlags.hasSocialMedia ? (
                  <DesignAgent
                    userEmail={userEmail}
                    subscription={subscriptionData}
                    onUpgradePlan={handleOpenPortal}
                    userCredits={userMeta?.credit || 0}
                    onRefreshCredits={refreshSubscriptionMeta}
                  />
                ) : assistantTab === "video" && featureFlags.hasSocialMedia ? (
                  <VideoAgent
                    subscription={subscriptionData}
                    onUpgrade={handleOpenPortal}
                  />
                ) : (
                  /* Fallback to Case Assistant */
                  <LenderChat
                    cases={cases}
                    caseFiles={caseFiles}
                    onOpenNewCase={openNewCaseModal}
                    onUpdateCaseFiles={setCaseFiles}
                    isLoading={showInitialLoader}
                    userCredits={userMeta?.credit || 0}
                  />
                )
              ) : currentView === "training" ? (
                <TrainingPortal />
              ) : currentView === "tasks" ? (
                <TasksDashboard
                  onNavigateToCase={(caseId) => {
                    const match = cases.find(c => c.id === caseId || c.caseId === caseId) ||
                                  collabCases.find(c => c.id === caseId || c.caseId === caseId);
                    if (match) handleCaseSelect(match);
                  }}
                  currentUserEmail={userEmail}
                  collabCases={collabCases}
                />
              ) : currentView === "support" ? (
                <SupportPage userEmail={userEmail} />
              ) : currentView === "developer" ? (
                <DeveloperPortal
                  userMeta={userMeta}
                  onUpgradePlan={handleStartCheckout}
                  onTopUp={handleTopUpCheckout}
                  creditPacks={creditPacks}
                  isPaymentProcessing={isPaymentProcessing}
                />
              ) : currentView === "provisioning" ? (
                <AccountProvisioning userEmail={userEmail} />
              ) : currentView === "refer-earn" ? (
                <ReferAndEarn userEmail={userEmail} />
              ) : currentView === "admin-usage" ? (
                <UsageDashboard userMeta={userMeta} />
              ) : currentView === "affiliate" ? (
                <AffiliatePortal userMeta={userMeta} />
              ) : currentView === "calendar" ? (
                <CalendarPage
                  currentUserEmail={userEmail}
                  cases={cases}
                />
              ) : currentView === "notes" ? (
                <NotesPage
                  currentUserEmail={userEmail}
                  onNavigateToCase={(caseId) => {
                    const match = cases.find(c => c.id === caseId || c.caseId === caseId);
                    if (match) handleCaseSelect(match);
                  }}
                  cases={cases}
                />
              ) : currentView === "organization" ? (
                <OrganizationManager
                  featureFlags={featureFlags}
                  subscription={subscriptionData}
                  userEmail={userEmail}
                  onUpgradePlan={handleOpenPortal}
                />
              ) : currentView === "leads" ? (
                <LeadsDashboard
                  userEmail={userEmail}
                  userMeta={userMeta}
                  teamMembers={teamMembers}
                  userCredits={userMeta?.credit || 0}
                  featureFlags={featureFlags}
                  onStartCall={handleStartPhoneCall}
                  onCreditsUpdate={async () => {
                    const subscriptionResponse = await fetchUserSubscription();
                    if (subscriptionResponse) {
                      setUserMetaSafe(prev => ({
                        ...prev,
                        credit: subscriptionResponse.credit || 0
                      }));
                    }
                  }}
                />
              ) : currentView === "partners" ? (
                <PartnersDashboard
                  userEmail={userEmail}
                  onNavigateToCase={(caseId) => {
                    setCurrentView("cases");
                    const targetCase = cases.find(c => c.id === caseId);
                    if (targetCase) setSelectedCase(targetCase);
                  }}
                />
              ) : currentView === "realtor-referral" ? (
                !featureFlags.hasRealtorReferral ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                        <Users className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                        Realtor Referral Center
                      </h2>
                      <p className="dark:text-slate-400 text-slate-500 mb-6">
                        Unlock the Realtor Referral Center with the Ultra plan.
                        Manage buyer referrals, track your pipeline, and grow your business with lender partnerships.
                      </p>
                      <button
                        onClick={handleOpenPortal}
                        className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-900/20"
                      >
                        Upgrade to Ultra
                      </button>
                    </div>
                  </div>
                ) : (
                  <RealtorReferralDashboard
                    userEmail={userEmail}
                  />
                )
              ) : currentView === "insurance-referral" ? (
                !featureFlags.hasInsuranceReferral ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
                        <Shield className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                        Insurance Referral Center
                      </h2>
                      <p className="dark:text-slate-400 text-slate-500 mb-6">
                        Unlock the Insurance Referral Center with the Ultra plan.
                        Track and manage insurance leads, view email history, and monitor pipeline performance.
                      </p>
                      <button
                        onClick={handleOpenPortal}
                        className="px-6 py-3 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-xl font-semibold hover:from-orange-500 hover:to-amber-500 transition-all shadow-lg shadow-orange-900/20"
                      >
                        Upgrade to Ultra
                      </button>
                    </div>
                  </div>
                ) : (
                  <InsuranceReferralDashboard
                    userEmail={userEmail}
                  />
                )
              ) : currentView === "sms" ? (
                !featureFlags.hasSMS ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center">
                        <MessageSquare className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                        SMS Messages
                      </h2>
                      <p className="dark:text-slate-400 text-slate-500 mb-6">
                        Unlock two-way SMS messaging with the Ultra plan.
                        Send personalized messages, use templates, and manage conversations with your clients.
                      </p>
                      <button
                        onClick={handleOpenPortal}
                        className="px-6 py-3 bg-gradient-to-r from-sky-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-sky-500 hover:to-indigo-500 transition-all shadow-lg shadow-sky-900/20"
                      >
                        Upgrade to Ultra
                      </button>
                    </div>
                  </div>
                ) : (
                  <SMSInbox
                    userEmail={userEmail}
                  />
                )
              ) : currentView === "fb-monitor" ? (
                !featureFlags.hasFBMonitor ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                        <Eye className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                        FB Content Monitor
                      </h2>
                      <p className="dark:text-slate-400 text-slate-500 mb-6">
                        Monitor Facebook groups and pages for mortgage leads.
                        Receive AI-generated daily reports with actionable insights.
                      </p>
                      <button
                        onClick={handleOpenPortal}
                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-500 hover:to-indigo-500 transition-all shadow-lg shadow-blue-900/20"
                      >
                        Upgrade to Ultra
                      </button>
                    </div>
                  </div>
                ) : (
                  <FBMonitorDashboard
                    userEmail={userEmail}
                    subscription={subscriptionData}
                  />
                )
              ) : currentView === "marketing" ? (
                !featureFlags.hasSocialMedia ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
                        <Megaphone className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                        Marketing Assistant
                      </h2>
                      <p className="dark:text-slate-400 text-slate-500 mb-6">
                        Unlock powerful marketing tools with the Ultra plan.
                        Create social media content, design graphics, and produce professional videos.
                      </p>
                      <button
                        onClick={handleOpenPortal}
                        className="px-6 py-3 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-xl font-semibold hover:from-pink-500 hover:to-rose-500 transition-all shadow-lg shadow-pink-900/20"
                      >
                        Upgrade to Ultra
                      </button>
                    </div>
                  </div>
                ) : (
                <div className="flex flex-col h-full">
                  {/* Marketing Tabs */}
                  <div className="flex items-center gap-1 px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50">
                    <button
                      onClick={() => setMarketingTab("social")}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        marketingTab === "social"
                          ? "bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300"
                          : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                      }`}
                    >
                      Social Media
                    </button>
                    {featureFlags.hasReels && (
                      <button
                        onClick={() => setMarketingTab("video")}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          marketingTab === "video"
                            ? "bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300"
                            : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                        }`}
                      >
                        Motion Lab
                      </button>
                    )}
                    {featureFlags.hasPhotoEditor && (
                      <button
                        onClick={() => setMarketingTab("design")}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          marketingTab === "design"
                            ? "bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300"
                            : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                        }`}
                      >
                        Design Studio
                      </button>
                    )}
                  </div>
                  {/* Marketing Content */}
                  <div className="flex-1 min-h-0 overflow-hidden">
                    {marketingTab === "social" ? (
                      <SocialMediaManager
                        userEmail={userEmail}
                        subscription={subscriptionData}
                        featureFlags={featureFlags}
                        onUpgradePlan={handleStartCheckout}
                      />
                    ) : marketingTab === "video" ? (
                      <MotionProjectsManager
                        onBack={() => setMarketingTab("social")}
                        featureFlags={featureFlags}
                      />
                    ) : marketingTab === "design" ? (
                      <PhotoProjectsManager
                        onBack={() => setMarketingTab("social")}
                        featureFlags={featureFlags}
                      />
                    ) : null}
                  </div>
                </div>
                )
              ) : currentView === "virtual-staging" ? (
                !featureFlags.hasVirtualStaging ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                        <Home className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                        Virtual Staging
                      </h2>
                      <p className="dark:text-slate-400 text-slate-500 mb-6">
                        Transform empty properties into beautifully staged homes with AI-powered virtual staging.
                        Available on Ultra plan.
                      </p>
                      <button
                        onClick={handleOpenPortal}
                        className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-900/20"
                      >
                        Upgrade to Ultra
                      </button>
                    </div>
                  </div>
                ) : (
                  <VirtualStaging
                    subscription={subscriptionData}
                    userEmail={userEmail}
                    userMeta={userMeta}
                    onUpgradePlan={handleOpenPortal}
                    onRefreshCredits={loadDashboard}
                  />
                )
              ) : currentView === "avatar-intro" ? (
                !featureFlags.hasVirtualStaging ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                        <Video className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                        Avatar Intro Videos
                      </h2>
                      <p className="dark:text-slate-400 text-slate-500 mb-6">
                        Generate AI-powered 8-second intro videos with your photo for real estate marketing.
                        Available on Ultra plan.
                      </p>
                      <button
                        onClick={handleOpenPortal}
                        className="px-6 py-3 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl font-semibold hover:from-teal-500 hover:to-cyan-500 transition-all shadow-lg shadow-teal-900/20"
                      >
                        Upgrade to Ultra
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 overflow-auto p-6 bg-[#f7f4ee] dark:bg-slate-950">
                    <AvatarIntro />
                  </div>
                )
              ) : currentView === "ai-images" ? (
                !featureFlags.hasSocialMedia ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                        AI Image Generator
                      </h2>
                      <p className="dark:text-slate-400 text-slate-500 mb-6">
                        Create stunning social media graphics with AI-powered image generation.
                        Available on Ultra plan.
                      </p>
                      <button
                        onClick={handleOpenPortal}
                        className="px-6 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-teal-500 hover:to-emerald-500 transition-all shadow-lg shadow-teal-900/20"
                      >
                        Upgrade to Ultra
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 overflow-auto bg-[#f7f4ee] dark:bg-slate-950">
                    <SocialImageGenerator
                      currentBrandId={null}
                      onUseInPostRequest={() => {
                        setMarketingTab("social");
                        setCurrentView("marketing");
                      }}
                    />
                  </div>
                )
              ) : currentView === "listings" ? (
                !featureFlags.hasListingsManagement ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
                        <MapPin className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                        Listings Management
                      </h2>
                      <p className="dark:text-slate-400 text-slate-500 mb-6">
                        Manage your property listings and track leads with our comprehensive listings dashboard.
                        Available on Ultra plan.
                      </p>
                      <button
                        onClick={handleOpenPortal}
                        className="px-6 py-3 bg-gradient-to-r from-rose-600 to-pink-600 text-white rounded-xl font-semibold hover:from-rose-500 hover:to-pink-500 transition-all shadow-lg shadow-rose-900/20"
                      >
                        Upgrade to Ultra
                      </button>
                    </div>
                  </div>
                ) : (
                  <ListingsDashboard />
                )
              ) : currentView === "direct-mail" ? (
                !featureFlags.hasDirectMail ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                        <Mail className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                        Direct Mail
                      </h2>
                      <p className="dark:text-slate-400 text-slate-500 mb-6">
                        Send USPS postcards to your mailing lists. Manage contacts, import CSV files, and track deliveries.
                        Available on Ultra plan.
                      </p>
                      <button
                        onClick={handleOpenPortal}
                        className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:from-indigo-500 hover:to-purple-500 transition-all shadow-lg shadow-indigo-900/20"
                      >
                        Upgrade to Ultra
                      </button>
                    </div>
                  </div>
                ) : (
                  <DirectMailDashboard
                    userEmail={userEmail}
                    userMeta={userMeta}
                    featureFlags={featureFlags}
                  />
                )
              ) : currentView === "social-scheduler" ? (
                !featureFlags.hasSocialScheduler ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                        <Megaphone className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
                        Social Scheduler
                      </h2>
                      <p className="dark:text-slate-400 text-slate-500 mb-6">
                        Schedule and publish posts to Instagram, Facebook, LinkedIn, TikTok and more - all from one dashboard.
                        Available on Ultra plan.
                      </p>
                      <button
                        onClick={handleOpenPortal}
                        className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:from-purple-500 hover:to-pink-500 transition-all shadow-lg shadow-purple-900/20"
                      >
                        Upgrade to Ultra
                      </button>
                    </div>
                  </div>
                ) : (
                  <SocialScheduler
                    userEmail={userEmail}
                    isAdmin={["andrew.uta99@gmail.com", "andrew.uta99@yahoo.com"].includes(userEmail?.toLowerCase())}
                  />
                )
              ) : (
                <div className="flex flex-col h-full bg-white dark:bg-slate-950 overflow-y-auto">
                  <div className="px-8 pt-6 pb-0 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30 flex items-center gap-8 sticky top-0 z-20">
                    <button
                      onClick={() => setDashboardTab('overview')}
                      className={`pb-4 text-sm font-bold uppercase tracking-wider transition-all border-b-2 ${dashboardTab === 'overview'
                        ? 'border-sky-500 text-slate-900 dark:text-white'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                        }`}
                    >
                      Executive Overview
                    </button>
                    <button
                      onClick={() => setDashboardTab('tasks')}
                      className={`pb-4 text-sm font-bold uppercase tracking-wider transition-all border-b-2 flex items-center gap-2 ${dashboardTab === 'tasks'
                        ? 'border-sky-500 text-slate-900 dark:text-white'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                        }`}
                    >
                      Tasks Dashboard
                    </button>
                    {featureFlags.hasReportsAnalytics && (
                      <button
                        onClick={() => setDashboardTab('reports')}
                        className={`pb-4 text-sm font-bold uppercase tracking-wider transition-all border-b-2 ${dashboardTab === 'reports'
                          ? 'border-sky-500 text-slate-900 dark:text-white'
                          : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                          }`}
                      >
                        Reports
                      </button>
                    )}
                    <button
                      onClick={() => setDashboardTab('usage')}
                      className={`pb-4 text-sm font-bold uppercase tracking-wider transition-all border-b-2 ${dashboardTab === 'usage'
                        ? 'border-sky-500 text-slate-900 dark:text-white'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                        }`}
                    >
                      Usage
                    </button>
                  </div>

                  <div className="flex-1 relative">
                    {dashboardTab === 'overview' ? (
                      <LenderPerformanceDashboard
                        dashboardData={transformCasesToDashboardData(cases, portfolioPnlSummary)}
                        pnlLoading={isPnlSummaryLoading}
                        pnlError={pnlSummaryError}
                        isCaseLoading={isCaseLoading}
                        onRefresh={loadDashboard}
                        isRefreshing={isRefreshing}
                        onAlertSelect={(alert) => {
                          const caseId = alert?.caseId;
                          const caseNumber = alert?.caseNumber;
                          const match = cases.find((item) =>
                            (caseId && (item.id === caseId || item.caseId === caseId || item.case_id === caseId)) ||
                            (caseNumber && item.caseNumber === caseNumber)
                          );
                          if (match) {
                            handleCaseSelect(match);
                          }
                        }}
                      />
                    ) : dashboardTab === 'reports' ? (
                      <ReportsAnalytics
                        featureFlags={featureFlags}
                        onUpgradePlan={handleOpenPortal}
                      />
                    ) : dashboardTab === 'usage' ? (
                      <UsageDashboard
                        userMeta={userMeta}
                        isAdmin={userMeta?.role === 'admin' || userMeta?.role === 'superadmin'}
                      />
                    ) : (
                      <TasksDashboard
                        onNavigateToCase={(caseId) => {
                          const match = cases.find(c => c.id === caseId || c.caseId === caseId) ||
                                        collabCases.find(c => c.id === caseId || c.caseId === caseId);
                          if (match) handleCaseSelect(match);
                        }}
                        currentUserEmail={userEmail}
                        collabCases={collabCases}
                      />
                    )}
                  </div>
                </div>
              )
              }
            </motion.div >
          </AnimatePresence >

          {/* Pagination */}
          {
            currentView === "cases" && filteredBorrowers.length > pageSize && (
              <div className="flex items-center justify-between mt-4 text-sm dark:text-slate-300 text-slate-600">
                <div>
                  Showing {Math.min(filteredBorrowers.length, (safePage - 1) * pageSize + 1)}-
                  {Math.min(filteredBorrowers.length, safePage * pageSize)} of {filteredBorrowers.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className={`px-3 py-1 rounded-lg border text-sm transition-colors ${safePage === 1
                      ? "opacity-50 cursor-not-allowed border-slate-200 text-slate-400"
                      : "dark:border-slate-700 dark:text-slate-200 border-slate-200 text-slate-700 hover:border-sky-500"
                      }`}
                  >
                    Prev
                  </button>
                  <span className="px-2 text-xs dark:text-slate-500 text-slate-400">Page {safePage} of {totalPages}</span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className={`px-3 py-1 rounded-lg border text-sm transition-colors ${safePage === totalPages
                      ? "opacity-50 cursor-not-allowed border-slate-200 text-slate-400"
                      : "dark:border-slate-700 dark:text-slate-200 border-slate-200 text-slate-700 hover:border-sky-500"
                      }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )
          }
        </div >
      </main >

      <NewCaseModal
        isNewCaseOpen={isNewCaseOpen}
        setIsNewCaseOpen={setIsNewCaseOpen}
        handleCaseSubmit={handleCaseSubmit}
        caseForm={caseForm}
        setCaseForm={setCaseForm}
        handleLoanAmountChange={handleLoanAmountChange}
        isSubmitting={isSubmitting}
        newCaseError={newCaseError}
        theme={theme}
      />

      <AccountSettingsModal
        isAccountSettingsOpen={isAccountSettingsOpen}
        setIsAccountSettingsOpen={setIsAccountSettingsOpen}
        userEmail={userEmail}
        userMeta={userMeta}
        handleLogout={onLogout}
        onUpdateMeta={setUserMetaSafe}
        onUpgradePlan={handleStartCheckout}
        onOpenBillingPortal={handleOpenPortal}
        isPaymentProcessing={isPaymentProcessing}
        featureFlags={featureFlags}
      />

      <ReportGenerationModal
        isOpen={isAiConfirmOpen}
        onClose={() => setIsAiConfirmOpen(false)}
        onConfirm={handleAiConfirm}
        onTopUp={(value) => {
          if (typeof value === "object" && value) {
            const lookup = value.lookupKey;
            const credits = Number(String(value.credits || "").replace(/[^0-9]/g, ""));
            if (lookup && lookup.startsWith("credits_")) {
              handleTopUpCheckout(lookup, {
                openInNewTab: Boolean(value.openInNewTab),
                expectedCredits: credits || undefined,
                source: 'ai-report'
              });
              return;
            }
            if (credits) {
              value = credits;
            } else {
              console.error("[TopUp] Invalid top up value:", value);
              setPaymentError("Unable to start top up checkout. Missing product configuration.");
              return;
            }
          }
          if (typeof value === "string") {
            if (value.startsWith("credits_")) {
              // Extract credits from lookup key (e.g., "credits_2000" -> 2000)
              const creditsFromKey = Number(value.replace("credits_", ""));
              handleTopUpCheckout(value, {
                expectedCredits: creditsFromKey || undefined,
                source: 'ai-report'
              });
              return;
            }
            const parsedCredits = Number(String(value).replace(/[^0-9]/g, ""));
            if (parsedCredits) {
              value = parsedCredits;
            } else {
              console.error("[TopUp] Invalid top up value:", value);
              setPaymentError("Unable to start top up checkout. Missing product configuration.");
              return;
            }
          }
          const credits = Number(value);
          const packFromApi = creditPacks.find((pack) => Number(String(pack.credits).replace(/[^0-9]/g, "")) === credits);
          const lookupMap = {
            2000: TOPUP_2000_LOOKUP_KEY,
            6000: TOPUP_6000_LOOKUP_KEY,
            30000: TOPUP_30000_LOOKUP_KEY
          };
          const lookupKey = packFromApi?.stripeLookupKey || lookupMap[credits];
          if (lookupKey) {
            handleTopUpCheckout(lookupKey, {
              expectedCredits: credits,
              source: 'ai-report'
            });
          } else {
            console.error("[TopUp] Missing lookupKey for credits:", credits);
            setPaymentError("Unable to start top up checkout. Missing product configuration.");
          }
        }}
        creditPacks={creditPacks}
        userTier={userMeta?.membership || 'free'}
        userCredits={userMeta?.credit || 0}
        isGenerating={isAiGenerating}
        isPaymentProcessing={isPaymentProcessing}
      />

      {/* OLD MODAL - TO BE REMOVED */}
      {
        false && isAiConfirmOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/80 px-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-3xl w-full shadow-2xl p-6 space-y-5 max-h-[85vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Choose Report Type</p>
                  <p className="text-lg font-bold text-white">Generate A.I. Underwriting Report</p>
                </div>
                <button onClick={() => setIsAiConfirmOpen(false)} className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-800 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-slate-800 rounded-2xl p-5 bg-slate-950/60 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-800/70 border border-slate-700 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-slate-200" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Basic</h3>
                      <p className="text-sm text-slate-400">Cost: 150 Credits</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-300">
                    Best for W2 employees with straightforward banking. The AI rapidly extracts data, calculates standard income, and checks for basic sufficient funds.
                  </p>
                  <div className="text-sm text-slate-300 space-y-1">
                    <p>✅ Extracts Income & Assets</p>
                    <p>✅ Basic DTI Calculation</p>
                    <p>✅ Document Data Validation</p>
                  </div>
                  <p className="text-xs text-slate-400">Best For: W2 Borrowers, Refinances, Clean Bank Statements.</p>
                  <button
                    onClick={() => handleAiConfirm({ reportType: "basic" })}
                    className="w-full px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors"
                  >
                    Choose Basic
                  </button>
                </div>
                <div className="border border-emerald-500/40 rounded-2xl p-5 bg-gradient-to-br from-emerald-500/10 via-slate-950/80 to-slate-950/60 shadow-[0_0_30px_rgba(16,185,129,0.15)] space-y-3 relative">

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-emerald-200" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Advanced Report</h3>
                      <p className="text-sm text-slate-400">Cost: 1,000 Credits</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-300">
                    An audit-grade analysis using our advanced Reasoning Engine. It thinks like a human underwriter to resolve discrepancies, cross-reference tax schedules against bank deposits, and draft Letters of Explanation.
                  </p>
                  <div className="text-sm text-slate-300 space-y-1">
                    <p>✅ Complex Income Analysis: (Schedule C, K-1, Corp)</p>
                    <p>✅ Forensic Asset Sourcing: Matches deposits to donor patterns.</p>
                    <p>✅ Logic Checks: Flags "silent" risks other tools miss.</p>
                  </div>
                  <p className="text-xs text-slate-400">Best For: Self-Employed, Investors (REO), Jumbo Loans, "Messy" File.</p>
                  <button
                    onClick={() => handleAiConfirm({ reportType: "advanced" })}
                    className="w-full px-4 py-2 rounded-lg bg-emerald-500 text-white font-semibold hover:bg-emerald-400 transition-colors shadow-[0_10px_30px_rgba(16,185,129,0.25)]"
                  >
                    Choose Advanced Report
                  </button>
                </div>
              </div>
              <div className="border-t border-slate-800 pt-4">
                <button
                  type="button"
                  onClick={() => setIsAiFaqOpen((prev) => !prev)}
                  className="w-full flex items-center justify-between gap-3 text-left"
                >
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">FAQs</p>
                  <ArrowRight
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isAiFaqOpen ? "rotate-90 text-sky-400" : ""}`}
                  />
                </button>
                {isAiFaqOpen && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    {[
                      { question: "Question 1", answer: "Answer 1" },
                      { question: "Question 2", answer: "Answer 2" },
                      { question: "Question 3", answer: "Answer 3" },
                      { question: "Question 4", answer: "Answer 4" },
                    ].map((faq, idx) => {
                      const isOpen = aiFaqOpenIndex === idx;
                      return (
                        <div
                          key={faq.question}
                          className="rounded-xl border border-slate-800 bg-slate-900/70 shadow-md shadow-black/20"
                        >
                          <button
                            type="button"
                            onClick={() => setAiFaqOpenIndex(isOpen ? null : idx)}
                            className="w-full flex items-center justify-between gap-2 px-3 py-3 text-left"
                          >
                            <span className="text-sm font-semibold text-white">{faq.question}</span>
                            <ArrowRight
                              className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-90 text-sky-400" : ""}`}
                            />
                          </button>
                          {isOpen && (
                            <div className="px-3 pb-3">
                              <p className="text-slate-400 leading-relaxed text-sm">{faq.answer}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="border border-emerald-200 rounded-2xl p-6 bg-gradient-to-r from-emerald-50 via-white to-slate-50 shadow-sm">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Top Up to Continue</p>
                    <p className="text-lg font-bold text-slate-900 mt-1">Unlock more underwriting runs instantly.</p>
                    <p className="text-sm text-slate-600 mt-1">Get immediate access to credits and keep files moving.</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4 mt-5">
                  <button className="text-left border border-emerald-200 rounded-xl px-4 py-4 bg-white hover:border-emerald-300 hover:shadow transition">
                    <p className="text-sm font-semibold text-slate-900">Buy 2,000 Credits - $20</p>
                    <p className="text-xs text-emerald-700 mt-1">Instant Access.</p>
                  </button>
                  <button className="text-left border border-emerald-300 rounded-xl px-4 py-4 bg-emerald-50 hover:border-emerald-400 hover:shadow transition">
                    <p className="text-sm font-semibold text-slate-900">Buy 6,000 Credits - $50</p>
                    <p className="text-xs text-emerald-700 mt-1">(Best Seller)</p>
                  </button>
                  <button className="text-left border border-slate-200 rounded-xl px-4 py-4 bg-white hover:border-emerald-300 hover:shadow transition">
                    <p className="text-sm font-semibold text-slate-900">Buy 30,000 Credits - $200</p>
                    <p className="text-xs text-emerald-700 mt-1">Save 33%.</p>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      <PreviewModal
        previewDoc={previewDoc}
        closePreview={closePreview}
        onSave={handleSavePreview}
      />

      <CaseDetailsModal
        selectedCase={selectedCase}
        onClose={() => setSelectedCase(null)}
        onStatusChange={handleStatusChange}
        onDocumentUpload={handleDocumentUpload}
        uploadMessage={uploadMessage}
        caseFiles={caseFiles}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        isStatusUpdating={isStatusUpdating}
        handlePreview={handlePreview}
        aiReports={selectedCase ? (aiReports[selectedCase.id || selectedCase.caseId || selectedCase.case_id] || []) : []}
        aiMessage={aiMessage}
        onGenerateAi={handleAiGenerateClick}
        caseError={caseError}
        isAiGenerating={isAiGenerating}
        aiPipelineStatus={pipelineStatus}
        pipelineRuns={pipelineRuns}
        isAccountSettingsOpen={isAccountSettingsOpen}
        caseComments={selectedCase ? (caseComments[selectedCase.id || selectedCase.caseId || selectedCase.case_id] || []) : []}
        onAddComment={handleAddComment}
        newComment={newComment}
        setNewComment={setNewComment}
        isCommenting={isCommenting}
        showSampleReport={showSampleReport}
        setShowSampleReport={setShowSampleReport}
        autoDownloadSample={autoDownloadSample}
        setAutoDownloadSample={setAutoDownloadSample}
        isCaseLoading={isCaseLoading}
        onDeleteFile={handleDeleteFile}
        isDeletingFile={isDeletingFile}
        fileDeleteTarget={fileDeleteTarget}
        isCaseHydrating={isCaseHydrating}
        onUpgradePlan={handleOpenPortal}
        featureFlags={featureFlags}
        userTier={userMeta?.membership || 'free'}
        userCredits={userMeta?.credit || 0}
        onRefreshCredits={refreshSubscriptionMeta}
        onCaseUpdate={handleCaseUpdate}
        userEmail={userEmail}
        onStartCall={handleStartPhoneCall}
      />
      {
        deleteCandidate && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 dark:bg-slate-950/80 bg-slate-500/50 backdrop-blur-sm">
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Delete Document</p>
                  <p className="text-lg font-bold dark:text-white text-slate-900 break-words">{deleteCandidate.name}</p>
                  <p className="text-sm dark:text-slate-400 text-slate-500 mt-1">Are you sure you want to delete this file?</p>
                </div>
                <button onClick={cancelDelete} className="dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 rounded-lg border dark:border-slate-700 border-slate-200 dark:text-slate-300 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  disabled={isDeletingFile}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteFile}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors flex items-center gap-2 disabled:opacity-60"
                  disabled={isDeletingFile}
                >
                  {isDeletingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        isDuplicatePromptOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 dark:bg-slate-950/80 bg-slate-500/50 backdrop-blur-sm">
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Duplicate files</p>
                  <p className="text-lg font-bold dark:text-white text-slate-900">Replace existing files?</p>
                  <p className="text-sm dark:text-slate-400 text-slate-500 mt-1">
                    These files already exist for this case:
                  </p>
                </div>
                <button
                  onClick={cancelDuplicateUpload}
                  className="p-2 rounded-full dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <ul className="text-sm dark:text-slate-200 text-slate-700 dark:bg-slate-950/60 bg-slate-100 border dark:border-slate-800 border-slate-200 rounded-lg p-3 space-y-1 max-h-32 overflow-y-auto">
                {duplicateFiles.map((name) => (
                  <li key={name} className="truncate">{name}</li>
                ))}
              </ul>
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelDuplicateUpload}
                  className="px-4 py-2 rounded-lg border dark:border-slate-700 border-slate-200 dark:text-slate-300 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDuplicateUpload}
                  className="px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors"
                >
                  Override files
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        isPaymentOpen && (
          <div className="fixed inset-0 z-[145] flex items-center justify-center p-4 dark:bg-slate-950/80 bg-slate-500/50 backdrop-blur-sm">
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl p-6 space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                    {(() => {
                      const storedType = typeof window !== "undefined"
                        ? window.localStorage.getItem("cm_checkout_type") || checkoutType
                        : checkoutType;
                      return storedType === "topup" ? "Top Up" : "Upgrade";
                    })()}
                  </p>
                  <p className="text-lg font-bold dark:text-white text-slate-900">
                    {(() => {
                      const storedType = typeof window !== "undefined"
                        ? window.localStorage.getItem("cm_checkout_type") || checkoutType
                        : checkoutType;
                      if (storedType === "topup") return "Credit Pack";

                      const storedTier = typeof window !== "undefined"
                        ? window.localStorage.getItem("cm_checkout_tier") || "starter"
                        : "starter";
                      return TIER_DISPLAY_INFO[storedTier]?.name || "Starter Membership";
                    })()}
                  </p>
                  {!checkoutSuccess && (
                    <p className="text-sm text-slate-400">
                      {(() => {
                        const storedType = typeof window !== "undefined"
                          ? window.localStorage.getItem("cm_checkout_type") || checkoutType
                          : checkoutType;
                        if (storedType === "topup") return "Purchase credits to keep reports moving.";

                        const storedTier = typeof window !== "undefined"
                          ? window.localStorage.getItem("cm_checkout_tier") || "starter"
                          : "starter";
                        return TIER_DISPLAY_INFO[storedTier]?.description || "50 cases • Fast processing • Priority support";
                      })()}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setIsPaymentOpen(false)}
                  className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {paymentError && (
                <div className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2">
                  {paymentError}
                </div>
              )}

              {/* Only show checkout message during processing or after success/failure - not for stale messages */}
              {checkoutMessage && (isPaymentProcessing || checkoutSuccess) && (
                <div className="text-sm text-slate-200 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2">
                  {checkoutMessage}
                </div>
              )}

              {isPaymentProcessing && (
                <div className="flex items-center gap-2 text-sm text-sky-200 bg-sky-500/10 border border-sky-500/30 rounded-lg px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{paymentStep || "Processing payment..."}</span>
                </div>
              )}

              {/* Show success state, or show form only when NOT processing */}
              {checkoutSuccess ? (
                <div className="space-y-4 text-sm text-slate-200">
                  <div className="flex items-start gap-3 bg-emerald-600/10 border border-emerald-500/40 rounded-lg p-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-300 font-bold">
                      ✓
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">Success</p>
                      <h4 className="text-lg font-semibold text-white">
                        {(() => {
                          // Read from localStorage as source of truth since state might not be updated yet
                          const storedType = typeof window !== "undefined"
                            ? window.localStorage.getItem("cm_checkout_type") || checkoutType
                            : checkoutType;
                          if (storedType === "topup") return "Credits added successfully!";

                          const storedTier = typeof window !== "undefined"
                            ? window.localStorage.getItem("cm_checkout_tier") || "starter"
                            : "starter";
                          const tierName = storedTier.charAt(0).toUpperCase() + storedTier.slice(1);
                          return `Subscription to ${tierName} Plan successful!`;
                        })()}
                      </h4>
                      <p className="text-sm text-slate-300">
                        {(() => {
                          const storedType = typeof window !== "undefined"
                            ? window.localStorage.getItem("cm_checkout_type") || checkoutType
                            : checkoutType;
                          return storedType === "topup"
                            ? "You can manage receipts in the Stripe customer portal."
                            : "Manage your billing details or receipts in the Stripe customer portal.";
                        })()}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleCheckoutSuccessAcknowledge}
                    disabled={isPaymentProcessing}
                    className="w-full py-2 rounded-lg bg-slate-100 text-slate-900 font-semibold hover:bg-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {isPaymentProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                    OK
                  </button>
                </div>
              ) : !isPaymentProcessing && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-slate-950/60 border border-slate-800 rounded-xl p-4">
                    <div className="flex items-center gap-4">
                      <div className="bg-slate-800 rounded-lg p-3">
                        <Logo className="h-12 w-auto" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Starter Plan</p>
                        <h3 className="text-2xl font-bold text-white">$20.00 / month</h3>
                        <p className="text-xs text-slate-400">500 monthly credits • 12-hour processing • Priority support</p>
                      </div>
                    </div>
                    <div className="text-right text-xs text-slate-400">
                      <p className="font-semibold text-slate-300">Stripe Checkout</p>
                      <p>PCI compliant • Redirect flow</p>
                    </div>
                  </div>

                  <button
                    id="checkout-and-portal-button"
                    type="button"
                    onClick={handleStartCheckout}
                    disabled={isPaymentProcessing}
                    className="w-full py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {isPaymentProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                    Start checkout
                  </button>

                  <button
                    type="button"
                    onClick={handleOpenPortal}
                    disabled={isPaymentProcessing}
                    className="w-full py-2 rounded-lg bg-slate-800 text-slate-100 font-semibold hover:bg-slate-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {isPaymentProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                    Manage billing (Stripe portal)
                  </button>

                  <p className="text-[11px] text-slate-500">
                    We'll redirect you to Stripe Checkout. On success, you'll land back here with a session id to open the
                    billing portal.
                  </p>
                </div>
              )}
            </div>
          </div>
        )
      }

      {/* Celebration Overlay - Confetti, Flying Emoji, Big Text */}
      <CelebrationOverlay state={celebrationState} />

      {/* Celebration Modal - Big and Bold */}
      <AnimatePresence>
        {celebrationStatus && celebrationStatus !== "success" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[170] flex items-center justify-center p-6 bg-slate-950/90 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.5, y: 100, rotateX: 45 }}
              animate={{ scale: 1, y: 0, rotateX: 0 }}
              exit={{ scale: 0.8, y: -50, opacity: 0 }}
              transition={{ type: "spring", bounce: 0.4, duration: 0.8 }}
              className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-2 border-emerald-500/50 rounded-3xl shadow-[0_0_100px_rgba(16,185,129,0.3)] w-full max-w-2xl overflow-hidden"
            >
              {/* Animated background glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 via-sky-500/10 to-purple-500/20 animate-pulse" />
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-emerald-500/30 rounded-full blur-3xl animate-bounce" style={{ animationDuration: '3s' }} />
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-sky-500/30 rounded-full blur-3xl animate-bounce" style={{ animationDuration: '2.5s', animationDelay: '0.5s' }} />

              <div className="relative p-12 space-y-6 text-center">
                {celebrationStatus === "Approved" ? (
                  <>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.3, type: "spring", bounce: 0.5 }}
                      className="text-8xl mb-4"
                    >
                      🎊
                    </motion.div>
                    <motion.h3
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-green-300 to-teal-400 drop-shadow-lg"
                    >
                      APPROVED!
                    </motion.h3>
                    <motion.p
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="text-slate-300 text-xl"
                    >
                      All required actions are complete. The loan is officially approved.
                    </motion.p>
                  </>
                ) : (
                  <>
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: 0.3, type: "spring", bounce: 0.5 }}
                      className="text-8xl mb-4"
                    >
                      🏆
                    </motion.div>
                    <motion.h3
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-300 to-orange-400 drop-shadow-lg"
                    >
                      CLEAR TO CLOSE!
                    </motion.h3>
                    <motion.p
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="text-slate-300 text-xl"
                    >
                      Underwriting is complete. This loan is ready for closing!
                    </motion.p>
                  </>
                )}
                <motion.button
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="mt-6 px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-lg font-bold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all"
                  onClick={() => {
                    setCelebrationStatus(null);
                    triggerCelebration(); // Trigger confetti, flying emoji, and celebration message
                  }}
                >
                  Continue
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <SampleSummaryModal
        isOpen={showSampleReport}
        onClose={() => {
          setShowSampleReport(false);
          setAutoDownloadSample(false);
        }}
        autoDownload={autoDownloadSample}
      />
      {/* Team Chat Widget */}
      <TaskCopilotPrototype userEmail={userEmail} initialConversations={chatConversations} onUnreadCountChange={setUnreadChatCount} />

      {/* AMA Assistant - Global AI Chat (Ultra+ only) */}
      {featureFlags.hasAMA && (
        <AMAChat userId={userEmail} onCaseClick={handleAMACaseClick} onTaskClick={handleAMATaskClick} />
      )}

      {/* Phone Call Modal - Global floating modal for calls */}
      <PhoneCallModal
        isOpen={phoneCall.isOpen}
        isMinimized={phoneCall.isMinimized}
        onClose={phoneCall.closeModal}
        onToggleMinimize={phoneCall.toggleMinimize}
        callStatus={phoneCall.callStatus}
        currentCall={phoneCall.currentCall}
        callDuration={phoneCall.callDuration}
        error={phoneCall.error}
        onHangUp={phoneCall.hangUp}
        onCCPReady={phoneCall.onCCPReady}
        setCallStatus={phoneCall.setCallStatus}
        setCurrentCall={phoneCall.setCurrentCall}
        setError={phoneCall.setError}
        startDurationTimer={phoneCall.startDurationTimer}
        stopDurationTimer={phoneCall.stopDurationTimer}
        setShowSummaryModal={phoneCall.setShowSummaryModal}
        isMuted={phoneCall.isMuted}
        isOnHold={phoneCall.isOnHold}
        isRecording={phoneCall.isRecording}
        onToggleMute={phoneCall.toggleMute}
        onToggleHold={phoneCall.toggleHold}
        onToggleRecording={phoneCall.toggleRecording}
        onSendDTMF={phoneCall.sendDTMF}
        transcript={phoneCall.transcript}
        isTranscribing={phoneCall.isTranscribing}
        callSummary={phoneCall.callSummary}
        isSummarizing={phoneCall.isSummarizing}
        showSummaryModal={phoneCall.showSummaryModal}
        onCloseSummaryModal={() => phoneCall.setShowSummaryModal(false)}
        onGenerateSummary={phoneCall.generateSummary}
        onSaveSummaryAsComment={phoneCall.saveSummaryAsComment}
      />
    </div >
  );
};

export default Dashboard;
